import {
  __name,
  noteItemsTextSchema,
  offerPurchaseOptionsSchema,
  offerTermsRowsSchema,
  orderTransitionActorSchema,
  pageQuerySchema,
  pageResponseSchema,
  sentAsReceived
} from "./chunk-LBZDZW6L.js";

// src/user/auth.schema.ts
import { NICKNAME_PATTERN, NICKNAME_RULE_MESSAGE, OAUTH_PROVIDERS, PolicyLanguage, SIMPLE_PIN_PATTERN, SocialPlatform } from "@inflip/shared";
import { z } from "zod";
var nicknameSchema = z.string().trim().min(1).max(100).regex(NICKNAME_PATTERN, NICKNAME_RULE_MESSAGE);
var payoutAccountSchema = z.object({
  bankName: z.string().min(1).max(50),
  accountNumber: z.string().min(4).max(50).regex(/^[\d-]+$/, "\uC22B\uC790\uC640 - \uB9CC \uC785\uB825"),
  holderName: z.string().min(1).max(50)
});
var simplePinSchema = z.string().regex(SIMPLE_PIN_PATTERN, "\uAC04\uD3B8\uBE44\uBC00\uBC88\uD638\uB294 \uC22B\uC790 6\uC790\uB9AC\uC608\uC694");
var createSimplePinRequestSchema = z.object({
  pin: simplePinSchema
});
var changeSimplePinRequestSchema = z.object({
  currentPin: simplePinSchema,
  newPin: simplePinSchema
});
var oauthProviderSchema = z.enum(OAUTH_PROVIDERS);
var oauthReauthCredentialSchema = z.object({
  provider: oauthProviderSchema,
  idToken: z.string().min(1).optional(),
  code: z.string().min(1).optional(),
  redirectUri: z.string().url().optional()
}).refine((v) => Boolean(v.idToken || v.code && v.redirectUri), {
  message: "\uC18C\uC15C \uB85C\uADF8\uC778 \uC815\uBCF4\uAC00 \uC5C6\uC5B4\uC694"
});
var updatePayoutAccountRequestSchema = payoutAccountSchema.extend({
  pin: simplePinSchema
});
var updateRefundAccountRequestSchema = payoutAccountSchema.extend({
  pin: simplePinSchema
});
var socialUrl = z.string().url("\uCC44\uB110 \uC8FC\uC18C\uB294 https:// \uB85C \uC2DC\uC791\uD558\uB294 \uC804\uCCB4 \uC8FC\uC18C\uB85C \uC785\uB825\uD574\uC8FC\uC138\uC694").max(500, "\uCC44\uB110 \uC8FC\uC18C\uB294 500\uC790 \uC774\uB0B4\uB85C \uC785\uB825\uD574\uC8FC\uC138\uC694").optional();
var socialLinksSchema = z.object({
  [SocialPlatform.YOUTUBE]: socialUrl,
  [SocialPlatform.INSTAGRAM]: socialUrl,
  [SocialPlatform.TIKTOK]: socialUrl,
  [SocialPlatform.THREADS]: socialUrl,
  [SocialPlatform.SOOP]: socialUrl,
  [SocialPlatform.PANDATV]: socialUrl
});
var updateSocialLinksRequestSchema = socialLinksSchema;
var updateMyProfileRequestSchema = z.object({
  name: nicknameSchema.optional(),
  phoneCountryCode: z.string().regex(/^\+\d{1,4}$/).optional(),
  phoneNumber: z.string().regex(/^[\d-]{4,20}$/, "\uC22B\uC790\uC640 - \uB9CC \uC785\uB825").or(z.literal("")).optional()
});
var emailSchema = z.string().trim().toLowerCase().email().max(320);
var signUpRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(8).max(128),
  name: nicknameSchema,
  /** 동의한 약관 slug 목록 — 동의 시점의 발행 버전이 기록된다 */
  agreedPolicySlugs: z.array(z.string()).max(20).optional(),
  /** 약관을 본 언어 — 동의 기록에 남는다 (없으면 한국어) */
  policyLanguage: z.nativeEnum(PolicyLanguage).optional()
});
var signInRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1),
  /** false 면 세션 쿠키 — 브라우저 종료 시 로그아웃 (기본 true) */
  rememberMe: z.boolean().optional()
});
var devSignInRequestSchema = z.object({
  email: emailSchema
});
var devSignInAccountsResponseSchema = z.object({
  accounts: z.array(z.object({
    name: z.string(),
    email: z.string()
  }))
});
var handoffSignInRequestSchema = z.object({
  code: z.string().min(32).max(128)
});
var logoutOthersResponseSchema = z.object({
  revoked: z.number().int()
});
var tokenPairResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int()
});
var meResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  payoutAccount: payoutAccountSchema.nullable().optional(),
  /** 구매자 환불 계좌 — 환불·입금 반환을 돌려받을 계좌 */
  refundAccount: payoutAccountSchema.nullable().optional(),
  /** 구매 신청을 했는데(취소되지 않은 구매 주문이 있음) 환불 계좌가 없다 — 웹이 등록 창을 띄운다 */
  refundAccountRequired: z.boolean().optional(),
  /** 정산 받을 거래(구매확정·지급 전 정산)가 있는데 정산 계좌가 없다 — 웹이 닫을 수 없는 등록 창을 띄운다 */
  payoutAccountRequired: z.boolean().optional(),
  socialLinks: socialLinksSchema.nullable().optional(),
  phoneCountryCode: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  /** 계정 시간대 (IANA) — 시각 표시 기준, 판매자일 때 주문에 고정되는 계약 시간대 */
  timeZone: z.string(),
  emailVerified: z.boolean(),
  /** 간편비밀번호를 만들었는지 — 정산 계좌·탈퇴 화면에서 '만들기' 와 '입력' 을 고른다 */
  hasSimplePin: z.boolean(),
  /** 가입 방식 — 간편비밀번호가 없을 때 탈퇴 본인 확인을 어느 소셜 계정으로 받을지 */
  authProvider: z.enum([
    "LOCAL",
    ...OAUTH_PROVIDERS
  ])
});
var refreshRequestSchema = z.object({
  /** 웹은 httpOnly 쿠키 사용 — 네이티브 앱만 body 로 전달 */
  refreshToken: z.string().min(1).optional()
});
var oauthSignInRequestSchema = z.object({
  provider: oauthProviderSchema,
  /** provider 가 발급한 ID 토큰 (Google Identity Services / Sign in with Apple) */
  idToken: z.string().min(1).optional(),
  /** Kakao: 인가 code — 서버가 kauth 토큰 엔드포인트에서 id_token 으로 교환 */
  code: z.string().min(1).optional(),
  redirectUri: z.string().url().optional(),
  /** Apple 은 최초 인증 시에만 이름을 클라이언트로 주므로 힌트로 전달 */
  name: z.string().max(100).optional()
}).superRefine((v, ctx) => {
  if (!v.idToken && !(v.code && v.redirectUri)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "idToken \uB610\uB294 (code + redirectUri) \uAC00 \uD544\uC694\uD569\uB2C8\uB2E4"
    });
  }
});
var oauthNeedsAgreementResponseSchema = z.object({
  needsAgreement: z.literal(true),
  pendingToken: z.string(),
  /** 미리 채워줄 프로필 (표시용) */
  email: z.string(),
  name: z.string().nullable()
});
var oauthSignInResponseSchema = z.union([
  tokenPairResponseSchema,
  oauthNeedsAgreementResponseSchema
]);
var completeOauthSignupRequestSchema = z.object({
  pendingToken: z.string().min(1),
  agreedPolicySlugs: z.array(z.string()).max(20),
  /** 약관을 본 언어 — 동의 기록에 남는다 (없으면 한국어) */
  policyLanguage: z.nativeEnum(PolicyLanguage).optional(),
  name: nicknameSchema.optional(),
  // ---- 선택 추가 정보 (안 적어도 가입 가능, 본인인증은 추후 별도) ----
  /** 국가 코드 — 예: '+82' */
  phoneCountryCode: z.string().regex(/^\+\d{1,4}$/).optional(),
  /** 전화번호 — 숫자/하이픈, 서버에서 숫자만 저장 */
  phoneNumber: z.string().regex(/^[\d-]{4,20}$/, "\uC22B\uC790\uC640 - \uB9CC \uC785\uB825").optional(),
  /** SNS/방송 채널 주소 — 입력한 것만 저장 */
  socialLinks: socialLinksSchema.optional()
});

// src/user/purchase-addons.schema.ts
import { BrandAsset, AppearanceMode, BrandLegalUse, UsagePlacement, UsageChannel, MerchandiseChannel } from "@inflip/shared";
import { z as z2 } from "zod";
var purchaseAddonsSchema = z2.object({
  appearanceMode: z2.nativeEnum(AppearanceMode).optional(),
  onsitePackageIds: z2.array(z2.string().min(1).max(80)).min(1).max(10).optional(),
  assets: z2.array(z2.nativeEnum(BrandAsset)).max(5).optional(),
  placements: z2.array(z2.nativeEnum(UsagePlacement)).max(Object.keys(UsagePlacement).length).optional(),
  legalUses: z2.array(z2.nativeEnum(BrandLegalUse)).max(3).optional(),
  channels: z2.array(z2.nativeEnum(UsageChannel)).max(8).optional(),
  merchandiseChannels: z2.array(z2.nativeEnum(MerchandiseChannel)).max(3).optional(),
  adultUsage: z2.boolean().optional()
}).strict();

// src/user/proposal-form.schema.ts
import { AppearanceMode as AppearanceMode2 } from "@inflip/shared";
import { z as z3 } from "zod";
var proposalFormSpecSchema = z3.object({
  appearanceMode: z3.nativeEnum(AppearanceMode2).nullable(),
  appearanceLabel: z3.string().nullable(),
  priceBasis: z3.string().nullable(),
  rightLabel: z3.string(),
  durations: z3.array(z3.object({
    months: z3.number().int(),
    label: z3.string(),
    addonMonths: z3.number().int()
  })),
  initialMonths: z3.number().int(),
  amountLabel: z3.string(),
  initialAmount: z3.number().int().nullable(),
  requirePositiveAmount: z3.boolean(),
  hasRoyalty: z3.boolean(),
  initialRoyaltyRateBp: z3.number().int(),
  initialRoyaltyPaymentDay: z3.number().int(),
  quantityFields: z3.array(z3.object({
    key: z3.enum([
      "includedQuantity",
      "totalSeconds"
    ]),
    label: z3.string(),
    unit: z3.string(),
    min: z3.number().int(),
    max: z3.number().int(),
    value: z3.number().int()
  })),
  quantityBaseText: z3.string().nullable(),
  optionGroups: z3.array(z3.object({
    label: z3.string(),
    options: z3.array(z3.object({
      group: z3.enum([
        "assets",
        "placements",
        "legalUses",
        "channels",
        "merchandiseChannels",
        "adultUsage"
      ]),
      key: z3.string(),
      label: z3.string(),
      /** null 미제공, 0 기본가 포함, 양수 고를 수 있는 (월) 추가금 */
      amount: z3.number().int().nullable()
    }))
  })),
  monthlyAddons: z3.boolean(),
  initialAddons: purchaseAddonsSchema,
  perCampaign: z3.boolean(),
  initialCampaignCount: z3.number().int(),
  appearanceNote: z3.boolean()
});

// src/user/listings.schema.ts
import { BrandAsset as BrandAsset2, appearanceMinimumPrice, appearancePackageMinimum as appearancePackageMinimum2, BrandExpression, UsageChannel as UsageChannel2, BrandLegalUse as BrandLegalUse2, Exclusivity, FORBIDDEN_INDUSTRY_LABELS, forbiddenIndustryKey, isPresetForbiddenIndustry, MAX_CUSTOM_FORBIDDEN_INDUSTRIES, MAX_FORBIDDEN_INDUSTRY_LENGTH, normalizeForbiddenIndustry, IpRightType as IpRightType4, ListingOfferStatus, ListingStatus, ModificationRight, RIGHT_TYPE_FORM_CONFIG, OFFER_DURATION_MONTHS as OFFER_DURATION_MONTHS2, PricingModel, UsagePlacement as UsagePlacement2, UsageRegion, ViolationPolicy, VoiceOfferingMode, UsagePurpose, getUsagePurpose, supportsUsagePurpose, isPersonalUsage, isMediaUsageScope, isGiveawayOnly, isAdultIndustryForbidden, MerchandiseProduct as MerchandiseProduct2, MerchandiseInventory as MerchandiseInventory2, VoiceSource, VoiceGenerationMethod, VoiceServiceCostBearer } from "@inflip/shared";
import { z as z7 } from "zod";

// src/user/appearance-scope.schema.ts
import { AppearanceMode as AppearanceMode3, AppearanceActivity, AppearanceTravel, AppearanceOvertime, AppearanceRecording, AppearanceChannel, IpRightType, OFFER_DURATION_MONTHS, appearanceActivityRecordings, appearanceAllowsPublicRecording, usesAppearanceSessionRecording, appearancePackageMinimum, hasAppearancePackageRecordings, appearancePackageRequiresRecording, withAppearancePackages, withAppearanceActivityRecordings, appearanceVariantMode, appearanceActivityLabel, appearanceActivityLabels, APPEARANCE_VARIANT_LABELS } from "@inflip/shared";
import { z as z4 } from "zod";
var appearancePackageSchema = z4.object({
  id: z4.string().min(1).max(80),
  recordingPolicy: z4.nativeEnum(AppearanceRecording).optional(),
  /** 옛 앱 호환 — 받기만 하고 읽지 않는다 (AppearancePackage.recordingRequired) */
  recordingRequired: z4.boolean().optional(),
  title: z4.string().trim().min(1, "\uAD6C\uC131 \uC774\uB984\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694.").max(80),
  durationMinutes: z4.number().int().min(15).max(1440).multipleOf(15),
  fixedAmount: z4.number().int().min(1e3).max(1e9),
  overtimePolicy: z4.nativeEnum(AppearanceOvertime),
  overtimeUnitMinutes: z4.number().int().refine((value) => value === 30 || value === 60).optional(),
  overtimeAmount: z4.number().int().min(1e3).max(1e9).optional()
}).strict();
var appearanceSessionSchema = z4.object({
  kind: z4.literal(IpRightType.APPEARANCE),
  mode: z4.nativeEnum(AppearanceMode3),
  activities: z4.array(z4.nativeEnum(AppearanceActivity)).max(7),
  /** 직접 추가한 활동 (단일 가격 현장 방문) — 옛 앱은 보내지 않는다 */
  customActivities: z4.array(z4.object({
    id: z4.string().min(1).max(80),
    title: z4.string().trim().min(1, "\uC9C1\uC811 \uCD94\uAC00\uD55C \uD65C\uB3D9 \uC774\uB984\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694.").max(40),
    policy: z4.nativeEnum(AppearanceRecording).optional()
  }).strict()).max(5).optional(),
  onsitePackages: z4.array(appearancePackageSchema).min(1).max(10).optional(),
  durationMinutes: z4.number().int().min(15).max(1440).multipleOf(15),
  serviceArea: z4.string().trim().max(300),
  travelPolicy: z4.nativeEnum(AppearanceTravel).optional(),
  travelNotes: z4.string().trim().max(500),
  overtimePolicy: z4.nativeEnum(AppearanceOvertime),
  overtimeUnitMinutes: z4.number().int().refine((value) => value === 30 || value === 60).optional(),
  overtimeAmount: z4.number().int().min(1e3).max(1e9).optional(),
  recordingPolicy: z4.nativeEnum(AppearanceRecording),
  activityRecordings: z4.array(z4.object({
    activity: z4.nativeEnum(AppearanceActivity),
    policy: z4.nativeEnum(AppearanceRecording)
  }).strict()).max(7).optional(),
  recordingMonths: z4.number().int().refine((v) => OFFER_DURATION_MONTHS.includes(v), "\uCD2C\uC601\uBB3C \uC0AC\uC6A9 \uAE30\uAC04\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.").optional(),
  channels: z4.array(z4.nativeEnum(AppearanceChannel)).max(4),
  channelAddons: z4.array(z4.object({
    key: z4.nativeEnum(AppearanceChannel),
    addonAmount: z4.number().int().min(1e3).max(1e9)
  }).strict()).max(4).optional(),
  cancellationNotes: z4.string().trim().min(1, "\uC77C\uC815 \uBCC0\uACBD\xB7\uCDE8\uC18C \uC870\uAC74\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694.").max(1e3),
  guidelines: z4.string().trim().max(1e3)
}).strict();
var appearanceScopeObjectSchema = appearanceSessionSchema.extend({
  variants: z4.array(appearanceSessionSchema.extend({
    fixedAmount: z4.number().int().min(1e3).max(1e9).nullable()
  })).min(1).max(3).optional()
});
function validateAppearanceScope(scope, ctx) {
  if (scope.variants) {
    const modes = scope.variants.map((v) => appearanceVariantMode(v.mode));
    if (new Set(modes).size !== modes.length) ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: [
        "variants"
      ],
      message: "\uAC19\uC740 \uCD9C\uC5F0 \uBC29\uC2DD\uC744 \uC911\uBCF5 \uB4F1\uB85D\uD560 \uC218 \uC5C6\uC5B4\uC694."
    });
    scope.variants.forEach((variant, index) => {
      const prefix = APPEARANCE_VARIANT_LABELS[appearanceVariantMode(variant.mode)];
      const child = {
        path: [
          ...ctx.path,
          "variants",
          index
        ],
        addIssue: /* @__PURE__ */ __name((issue2) => ctx.addIssue({
          ...issue2,
          path: [
            "variants",
            index,
            ...issue2.path ?? []
          ],
          message: `${prefix}: ${issue2.message}`
        }), "addIssue")
      };
      validateAppearanceScope(variant, child);
      if (variant.onsitePackages && variant.fixedAmount !== appearancePackageMinimum(variant.onsitePackages)) child.addIssue({
        code: z4.ZodIssueCode.custom,
        path: [
          "fixedAmount"
        ],
        message: "\uD604\uC7A5 \uBC29\uBB38 \uAD6C\uC131 \uAC00\uACA9\uACFC \uB300\uD45C \uAC00\uACA9\uC774 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC544\uC694."
      });
      if (variant.activities.some((a) => !appearanceActivityLabels(variant.mode)[a])) child.addIssue({
        code: z4.ZodIssueCode.custom,
        path: [
          "activities"
        ],
        message: "\uC120\uD0DD\uD55C \uCD9C\uC5F0 \uBC29\uC2DD\uC5D0 \uB9DE\uB294 \uD65C\uB3D9\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694."
      });
    });
    return;
  }
  const issue = /* @__PURE__ */ __name((field, message) => ctx.addIssue({
    code: z4.ZodIssueCode.custom,
    path: [
      field
    ],
    message
  }), "issue");
  const custom = scope.customActivities ?? [];
  if (!scope.activities.length && !custom.length && scope.mode === AppearanceMode3.ONSITE && !scope.onsitePackages) issue("activities", "\uD3EC\uD568\uD560 \uCD9C\uC5F0 \uD65C\uB3D9\uC744 1\uAC1C \uC774\uC0C1 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.");
  if (custom.length) {
    if (scope.mode !== AppearanceMode3.ONSITE || scope.onsitePackages) issue("customActivities", "\uD65C\uB3D9 \uC9C1\uC811 \uCD94\uAC00\uB294 \uB2E8\uC77C \uAC00\uACA9 \uD604\uC7A5 \uBC29\uBB38\uC5D0\uC11C\uB9CC \uD560 \uC218 \uC788\uC5B4\uC694.");
    const titles = [
      ...scope.activities.map((a) => appearanceActivityLabel(scope.mode, a)),
      ...custom.map((item) => item.title)
    ];
    if (new Set(custom.map((item) => item.id)).size !== custom.length || new Set(titles).size !== titles.length) issue("customActivities", "\uD65C\uB3D9 \uC774\uB984\uC744 \uC11C\uB85C \uB2E4\uB974\uAC8C \uC785\uB825\uD574 \uC8FC\uC138\uC694.");
    if (custom.some((item) => !item.policy)) issue("customActivities", "\uC9C1\uC811 \uCD94\uAC00\uD55C \uD65C\uB3D9\uB9C8\uB2E4 \uCD2C\uC601\xB7\uC0AC\uC6A9 \uD5C8\uC6A9 \uBC94\uC704\uB97C \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.");
  }
  if (scope.onsitePackages) {
    if (scope.mode !== AppearanceMode3.ONSITE) issue("onsitePackages", "\uD604\uC7A5 \uBC29\uBB38\uC5D0\uC11C\uB9CC \uAD6C\uC131\uC744 \uC124\uC815\uD560 \uC218 \uC788\uC5B4\uC694.");
    if (new Set(scope.onsitePackages.map((p) => p.id)).size !== scope.onsitePackages.length) issue("onsitePackages", "\uAC19\uC740 \uAD6C\uC131\uC744 \uC911\uBCF5 \uB4F1\uB85D\uD560 \uC218 \uC5C6\uC5B4\uC694.");
    if (new Set(scope.onsitePackages.map((p) => p.title)).size !== scope.onsitePackages.length) issue("onsitePackages", "\uAD6C\uC131 \uC774\uB984\uC744 \uC11C\uB85C \uB2E4\uB974\uAC8C \uC785\uB825\uD574 \uC8FC\uC138\uC694.");
    if (scope.onsitePackages.some((p) => p.recordingPolicy !== void 0) && !hasAppearancePackageRecordings(scope)) issue("onsitePackages", "\uAC01 \uAD6C\uC131\uC758 \uCD2C\uC601\xB7\uC0AC\uC6A9 \uD5C8\uC6A9 \uBC94\uC704\uB97C \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.");
    if (hasAppearancePackageRecordings(scope)) {
      if (scope.activities.length || scope.activityRecordings?.length || scope.customActivities?.length) issue("onsitePackages", "\uAD6C\uC131\uBCC4 \uCD2C\uC601 \uC870\uAC74\uACFC \uAE30\uC874 \uD65C\uB3D9\uBCC4 \uC870\uAC74\uC744 \uD568\uAED8 \uC124\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694.");
      if (withAppearancePackages(scope, scope.onsitePackages).recordingPolicy !== scope.recordingPolicy) issue("onsitePackages", "\uAD6C\uC131\uBCC4 \uCD2C\uC601 \uD5C8\uC6A9 \uBC94\uC704\uC640 \uACF5\uAC1C \uC0AC\uC6A9 \uC870\uAC74\uC774 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC544\uC694.");
    }
    for (const p of scope.onsitePackages) {
      if (appearancePackageRequiresRecording(p) && p.recordingPolicy === AppearanceRecording.NOT_ALLOWED) issue("onsitePackages", `${p.title}: \uCD2C\uC601 \uAD6C\uC131\uC740 \uB0B4\uBD80 \uAE30\uB85D \uB610\uB294 \uACF5\uAC1C \uC0AC\uC6A9 \uC911 \uD5C8\uC6A9\uD560 \uBC94\uC704\uB97C \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.`);
      if (p.overtimePolicy === AppearanceOvertime.PAID ? !p.overtimeUnitMinutes || !p.overtimeAmount : p.overtimeUnitMinutes !== void 0 || p.overtimeAmount !== void 0) issue("onsitePackages", `${p.title}: \uC5F0\uC7A5 \uB2E8\uC704\uC640 \uCD94\uAC00\uAE08\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694.`);
    }
  }
  if (new Set(scope.activities).size !== scope.activities.length) issue("activities", "\uAC19\uC740 \uCD9C\uC5F0 \uD65C\uB3D9\uC744 \uC911\uBCF5 \uC120\uD0DD\uD560 \uC218 \uC5C6\uC5B4\uC694.");
  if (scope.mode === AppearanceMode3.ONSITE) {
    if (!scope.travelPolicy) issue("travelPolicy", "\uAD50\uD1B5\xB7\uC219\uBC15\uBE44 \uC870\uAC74\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.");
    if (scope.travelPolicy === AppearanceTravel.SEPARATE && !scope.travelNotes) issue("travelNotes", "\uBCC4\uB3C4 \uAD50\uD1B5\xB7\uC219\uBC15\uBE44\uC758 \uC0B0\uC815\xB7\uD569\uC758 \uAE30\uC900\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694.");
  } else if (scope.serviceArea || scope.travelPolicy || scope.travelNotes) issue("mode", "\uC628\uB77C\uC778 \uCC38\uC5EC\uC5D0\uB294 \uBC29\uBB38 \uC9C0\uC5ED\uACFC \uAD50\uD1B5\xB7\uC219\uBC15\uBE44\uB97C \uC124\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694.");
  if (scope.overtimePolicy === AppearanceOvertime.PAID) {
    if (!scope.overtimeUnitMinutes || !scope.overtimeAmount) issue("overtimeAmount", "\uC2DC\uAC04 \uCD08\uACFC \uC2DC \uC801\uC6A9\uD560 \uB2E8\uC704\uC640 \uCD94\uAC00\uAE08\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694.");
  } else if (scope.overtimeUnitMinutes !== void 0 || scope.overtimeAmount !== void 0) issue("overtimePolicy", "\uC815\uD574\uC9C4 \uCD94\uAC00\uAE08\uC73C\uB85C \uC5F0\uC7A5\uD560 \uB54C\uB9CC \uC5F0\uC7A5 \uAE08\uC561\uC744 \uC124\uC815\uD560 \uC218 \uC788\uC5B4\uC694.");
  const channels = [
    ...scope.channels,
    ...(scope.channelAddons ?? []).map((o) => o.key)
  ];
  if (new Set(channels).size !== channels.length) issue("channels", "\uAC19\uC740 \uCD2C\uC601\uBB3C \uC0AC\uC6A9 \uC704\uCE58\uB97C \uC911\uBCF5 \uC120\uD0DD\uD560 \uC218 \uC5C6\uC5B4\uC694.");
  if (scope.activityRecordings || custom.length) {
    const recordings = scope.activityRecordings ?? [];
    const activities = recordings.map((item) => item.activity);
    if (new Set(activities).size !== activities.length || activities.length !== scope.activities.length || activities.some((activity) => !scope.activities.includes(activity))) issue("activityRecordings", "\uC120\uD0DD\uD55C \uD65C\uB3D9\uB9C8\uB2E4 \uCD2C\uC601\xB7\uC0AC\uC6A9 \uD5C8\uC6A9 \uBC94\uC704\uB97C \uD558\uB098\uC529 \uC124\uC815\uD574 \uC8FC\uC138\uC694.");
    if (!usesAppearanceSessionRecording(scope) && !hasAppearancePackageRecordings(scope) && withAppearanceActivityRecordings(scope, recordings, scope.activities).recordingPolicy !== scope.recordingPolicy) issue("activityRecordings", "\uD65C\uB3D9\uBCC4 \uCD2C\uC601\xB7\uC0AC\uC6A9 \uBC94\uC704\uC640 \uACF5\uAC1C \uC0AC\uC6A9 \uC870\uAC74\uC774 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC544\uC694.");
  }
  if (appearanceAllowsPublicRecording(scope)) {
    if (!scope.channels.length && !scope.channelAddons?.length) issue("channels", "\uCD2C\uC601\uBB3C \uC0AC\uC6A9 \uC704\uCE58\uB97C 1\uAC1C \uC774\uC0C1 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.");
    if (!scope.recordingMonths) issue("recordingMonths", "\uCD2C\uC601\uBB3C \uC0AC\uC6A9 \uAE30\uAC04\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.");
  } else if (channels.length || scope.recordingMonths !== void 0) issue("activityRecordings", "\uACF5\uAC1C \uC0AC\uC6A9\uC744 \uD5C8\uC6A9\uD560 \uB54C\uB9CC \uCD2C\uC601\uBB3C \uC0AC\uC6A9 \uC704\uCE58\xB7\uAE30\uAC04\uC744 \uC124\uC815\uD560 \uC218 \uC788\uC5B4\uC694.");
  if (appearanceActivityRecordings(scope).some(({ activity, policy }) => policy === AppearanceRecording.NOT_ALLOWED && (activity === AppearanceActivity.PHOTO || activity === AppearanceActivity.VIDEO))) issue("activityRecordings", "\uCD2C\uC601 \uCD9C\uC5F0\uC744 \uD3EC\uD568\uD558\uBA74 \uB0B4\uBD80 \uAE30\uB85D \uB610\uB294 \uACF5\uAC1C \uC0AC\uC6A9 \uC911 \uD5C8\uC6A9\uD560 \uBC94\uC704\uB97C \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.");
}
__name(validateAppearanceScope, "validateAppearanceScope");

// src/user/co-branding-scope.schema.ts
import { CoBrandingAsset, CoBrandingChannel, CoBrandingExpiry, IpRightType as IpRightType2 } from "@inflip/shared";
import { z as z5 } from "zod";
var coBrandingScopeObjectSchema = z5.object({
  kind: z5.literal(IpRightType2.CO_BRANDING),
  assets: z5.array(z5.nativeEnum(CoBrandingAsset)).min(1).max(3),
  campaignPricing: z5.literal("PER_CAMPAIGN"),
  campaignCount: z5.number().int().min(1).max(100),
  channels: z5.array(z5.nativeEnum(CoBrandingChannel)).min(1).max(4),
  channelAddons: z5.array(z5.object({
    key: z5.nativeEnum(CoBrandingChannel),
    addonAmount: z5.number().int().min(1e3).max(1e9)
  }).strict()).max(4).optional(),
  adultUsage: z5.object({
    addonAmount: z5.number().int().min(0).max(1e9)
  }).strict().optional(),
  expiryPolicy: z5.nativeEnum(CoBrandingExpiry),
  delivery: z5.string().trim().max(500),
  rights: z5.string().trim().max(1e3)
}).strict();

// src/user/merchandise-scope.schema.ts
import { IpRightType as IpRightType3, MerchandiseDistribution, MerchandiseProduct, MerchandiseAsset, MerchandiseChannel as MerchandiseChannel2, MerchandiseReproduction, MerchandiseInventory, MERCHANDISE_MAX_QUANTITY, josa } from "@inflip/shared";
import { z as z6 } from "zod";
var selections = /* @__PURE__ */ __name((values, label) => z6.array(z6.nativeEnum(values)).min(1, `${josa(label, "\uC744", "\uB97C")} 1\uAC1C \uC774\uC0C1 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.`).max(Object.keys(values).length), "selections");
var merchandiseScopeObjectSchema = z6.object({
  kind: z6.literal(IpRightType3.MERCHANDISE),
  distributionModes: selections(MerchandiseDistribution, "\uD310\uB9E4\xB7\uBC30\uD3EC \uBC29\uC2DD"),
  products: selections(MerchandiseProduct, "\uC81C\uC791 \uD488\uBAA9"),
  otherProduct: z6.string().trim().max(100),
  assets: selections(MerchandiseAsset, "\uC0AC\uC6A9\uD560 \uC694\uC18C"),
  includedQuantity: z6.number().int().min(1).max(MERCHANDISE_MAX_QUANTITY),
  distributionChannels: selections(MerchandiseChannel2, "\uD310\uB9E4\xB7\uBC30\uD3EC \uACBD\uB85C"),
  distributionChannelAddons: z6.array(z6.object({
    key: z6.nativeEnum(MerchandiseChannel2),
    addonAmount: z6.number().int().min(1e3).max(1e9)
  }).strict()).max(3).optional(),
  adultUsage: z6.object({
    addonAmount: z6.number().int().min(0).max(1e9)
  }).strict().optional(),
  additionalProduction: z6.nativeEnum(MerchandiseReproduction),
  sampleApprovalRequired: z6.boolean(),
  inventoryPolicy: z6.nativeEnum(MerchandiseInventory),
  sellOffDays: z6.number().int().min(1).max(365).optional(),
  delivery: z6.string().trim().max(500),
  rights: z6.string().trim().max(1e3),
  guidelines: z6.string().trim().max(1e3)
}).strict();

// src/user/listings.schema.ts
var listListingsQuerySchema = pageQuerySchema.extend({
  q: z7.string().max(200).optional(),
  rightType: z7.nativeEnum(IpRightType4).optional(),
  sort: z7.enum([
    "latest",
    "price_asc",
    "price_desc"
  ]).default("latest")
});
var offerDurationSchema = z7.number().int().refine((v) => v === 0 || OFFER_DURATION_MONTHS2.includes(v), {
  message: "\uC0AC\uC6A9 \uAE30\uAC04\uC740 1/3/6/12\uAC1C\uC6D4 (\uB610\uB294 \uAE30\uAC04 \uC5C6\uC74C 0) \uC911 \uD558\uB098"
});
var offerDurationOptionSchema = z7.object({
  months: offerDurationSchema,
  /** null = 가격 협의 (구매 대신 제안으로 진행). REVENUE_SHARE 는 항상 null */
  fixedAmount: z7.number().int().positive().max(1e9).nullish()
});
var brandScopeOption = /* @__PURE__ */ __name((values) => z7.object({
  key: z7.nativeEnum(values),
  addonAmount: z7.number().int().min(0).max(1e9)
}), "brandScopeOption");
var brandScopeSchema = z7.object({
  assets: z7.array(brandScopeOption(BrandAsset2)).min(1).max(5),
  placements: z7.array(brandScopeOption(UsagePlacement2)).min(1).max(Object.keys(UsagePlacement2).length),
  legalUses: z7.array(brandScopeOption(BrandLegalUse2)).max(3).default([])
}).superRefine((scope, ctx) => {
  for (const [path, items] of Object.entries(scope)) {
    if (new Set(items.map((i) => i.key)).size !== items.length) {
      ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        path: [
          path
        ],
        message: "\uD56D\uBAA9\uC774 \uC911\uBCF5\uB429\uB2C8\uB2E4"
      });
    }
  }
});
var scopeBase = {
  guidelines: z7.string().trim().max(1e3)
};
var mediaScope = {
  ...scopeBase,
  channels: z7.array(z7.nativeEnum(UsageChannel2)).max(7),
  channelAddons: z7.array(z7.object({
    key: z7.nativeEnum(UsageChannel2),
    addonAmount: z7.number().int().min(1e3).max(1e9)
  })).max(7).optional(),
  includedQuantity: z7.number().int().min(1).max(1e3),
  delivery: z7.string().trim().max(500),
  rights: z7.string().trim().max(1e3)
};
var usageScopeSchema = z7.discriminatedUnion("kind", [
  appearanceScopeObjectSchema,
  merchandiseScopeObjectSchema,
  coBrandingScopeObjectSchema,
  z7.object({
    ...scopeBase,
    kind: z7.literal(IpRightType4.NAME_BRAND),
    brandName: z7.string().trim().min(1, "\uC0AC\uC6A9\uD560 \uC774\uB984\xB7\uBE0C\uB79C\uB4DC\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694.").max(200),
    expressions: z7.array(z7.nativeEnum(BrandExpression)).min(1).max(3)
  }),
  z7.object({
    ...mediaScope,
    purpose: z7.nativeEnum(UsagePurpose).optional(),
    kind: z7.literal(IpRightType4.FACE_PHOTO)
  }),
  z7.object({
    ...mediaScope,
    purpose: z7.nativeEnum(UsagePurpose).optional(),
    kind: z7.literal(IpRightType4.CONTENT_SECONDARY)
  }),
  z7.object({
    ...mediaScope,
    kind: z7.literal(IpRightType4.AI_VOICE),
    purpose: z7.nativeEnum(UsagePurpose).optional(),
    mode: z7.nativeEnum(VoiceOfferingMode),
    source: z7.nativeEnum(VoiceSource),
    totalSeconds: z7.number().int().min(1).max(36e3),
    generation: z7.object({
      method: z7.nativeEnum(VoiceGenerationMethod),
      languages: z7.string().trim().min(1).max(200),
      generationDays: z7.number().int().min(1).max(365),
      costBearer: z7.nativeEnum(VoiceServiceCostBearer),
      scriptApprovalRequired: z7.boolean()
    }).optional(),
    production: z7.object({
      scriptMaxLength: z7.number().int().min(1).max(1e5),
      deliveryDays: z7.number().int().min(1).max(365),
      revisions: z7.number().int().min(0).max(100)
    }).optional()
  })
]).superRefine((scope, ctx) => {
  if (scope.kind === IpRightType4.APPEARANCE) validateAppearanceScope(scope, ctx);
  if (scope.kind === IpRightType4.CO_BRANDING) {
    for (const key of [
      "assets",
      "channels"
    ]) {
      if (new Set(scope[key]).size !== scope[key].length) ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        path: [
          key
        ],
        message: "\uAC19\uC740 \uD56D\uBAA9\uC744 \uC911\uBCF5 \uC120\uD0DD\uD560 \uC218 \uC5C6\uC5B4\uC694."
      });
    }
    const channels = [
      ...scope.channels,
      ...(scope.channelAddons ?? []).map((item) => item.key)
    ];
    if (new Set(channels).size !== channels.length) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "channelAddons"
      ],
      message: "\uAC19\uC740 \uC0AC\uC6A9 \uC704\uCE58\uB97C \uAE30\uBCF8\uAC00\uC640 \uCD94\uAC00\uAE08\uC5D0 \uC911\uBCF5 \uC124\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694."
    });
  }
  if (scope.kind === IpRightType4.MERCHANDISE) {
    const channels = [
      ...scope.distributionChannels,
      ...(scope.distributionChannelAddons ?? []).map((item) => item.key)
    ];
    if (new Set(channels).size !== channels.length) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "distributionChannelAddons"
      ],
      message: "\uAC19\uC740 \uACBD\uB85C\uB97C \uAE30\uBCF8\uAC00\uC640 \uCD94\uAC00\uAE08\uC5D0 \uC911\uBCF5 \uC124\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694."
    });
    for (const key of [
      "distributionModes",
      "products",
      "assets",
      "distributionChannels"
    ]) {
      if (new Set(scope[key]).size !== scope[key].length) ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        path: [
          key
        ],
        message: "\uAC19\uC740 \uD56D\uBAA9\uC744 \uC911\uBCF5 \uC120\uD0DD\uD560 \uC218 \uC5C6\uC5B4\uC694."
      });
    }
    if (scope.products.includes(MerchandiseProduct2.OTHER) && !scope.otherProduct.trim()) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "otherProduct"
      ],
      message: "\uAE30\uD0C0 \uC81C\uC791 \uD488\uBAA9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694."
    });
    if (!scope.products.includes(MerchandiseProduct2.OTHER) && scope.otherProduct) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "otherProduct"
      ],
      message: "\uAE30\uD0C0 \uD488\uBAA9\uC744 \uC120\uD0DD\uD558\uAC70\uB098 \uC785\uB825\uD55C \uAE30\uD0C0 \uD488\uBAA9\uC744 \uBE44\uC6CC \uC8FC\uC138\uC694."
    });
    if (scope.inventoryPolicy === MerchandiseInventory2.SELL_OFF && scope.sellOffDays == null) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "sellOffDays"
      ],
      message: "\uAE30\uC874 \uC7AC\uACE0\uC758 \uD310\uB9E4\xB7\uBC30\uD3EC \uD5C8\uC6A9 \uAE30\uAC04\uC744 1~365\uC77C\uB85C \uC124\uC815\uD574 \uC8FC\uC138\uC694."
    });
    if (scope.inventoryPolicy !== MerchandiseInventory2.SELL_OFF && scope.sellOffDays != null) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "sellOffDays"
      ],
      message: "\uAE30\uC874 \uC7AC\uACE0\uC758 \uD310\uB9E4\xB7\uBC30\uD3EC\uB97C \uD5C8\uC6A9\uD560 \uB54C\uB9CC \uC815\uB9AC \uAE30\uAC04\uC744 \uC124\uC815\uD560 \uC218 \uC788\uC5B4\uC694."
    });
  }
  if (scope.kind === IpRightType4.AI_VOICE) {
    if (scope.mode === VoiceOfferingMode.CUSTOM && !scope.production) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "production"
      ],
      message: "\uB300\uC0AC \uBD84\uB7C9\xB7\uCD08\uC548 \uC804\uB2EC \uAE30\uD55C\xB7\uC218\uC815 \uD69F\uC218\uB97C \uC124\uC815\uD574 \uC8FC\uC138\uC694."
    });
    if (scope.mode !== VoiceOfferingMode.CUSTOM && scope.production) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "production"
      ],
      message: "\uC0C8 \uC74C\uC131 \uC81C\uC791\xB7\uC804\uB2EC \uBC29\uC2DD\uC5D0\uB9CC \uC81C\uC791 \uC870\uAC74\uC744 \uD3EC\uD568\uD560 \uC218 \uC788\uC5B4\uC694."
    });
    if (scope.mode === VoiceOfferingMode.BUYER_GENERATED) {
      if (!scope.generation) ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        path: [
          "generation"
        ],
        message: "AI \uC74C\uC131 \uC0DD\uC131 \uBC29\uBC95\xB7\uC11C\uBE44\uC2A4\xB7\uD5C8\uC6A9 \uC5B8\uC5B4\xB7\uAE30\uAC04\uC744 \uC124\uC815\uD574 \uC8FC\uC138\uC694."
      });
      if (scope.source !== VoiceSource.AI_GENERATED) ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        path: [
          "source"
        ],
        message: "\uAD6C\uB9E4\uC790\uAC00 \uC0DD\uC131\uD558\uB294 \uC74C\uC131\uC740 AI \uC0DD\uC131 \uBC29\uC2DD\uC774\uC5B4\uC57C \uD574\uC694."
      });
    } else if (scope.generation) {
      ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        path: [
          "generation"
        ],
        message: "\uAD6C\uB9E4\uC790\uAC00 AI \uC74C\uC131 \uC0DD\uC131 \uBC29\uC2DD\uC5D0\uB9CC \uC0DD\uC131 \uC870\uAC74\uC744 \uD3EC\uD568\uD560 \uC218 \uC788\uC5B4\uC694."
      });
    }
    const unsupported = [
      UsageChannel2.PACKAGING,
      UsageChannel2.PRODUCT_NAME
    ];
    if ([
      ...scope.channels,
      ...(scope.channelAddons ?? []).map((item) => item.key)
    ].some((key) => unsupported.includes(key))) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "channels"
      ],
      message: "\uC74C\uC131 \uC0AC\uC6A9\uAD8C\uC5D0\uC11C \uC81C\uACF5\uD560 \uC218 \uC5C6\uB294 \uC0AC\uC6A9 \uC704\uCE58\uAC00 \uD3EC\uD568\uB418\uC5B4 \uC788\uC5B4\uC694."
    });
  }
  if (isMediaUsageScope(scope)) {
    if (isPersonalUsage(scope)) {
      if (scope.channels.length || scope.channelAddons?.length) ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        path: [
          "purpose"
        ],
        message: "\uAC1C\uC778 \uAC10\uC0C1\uC6A9\uC5D0\uB294 \uC0AC\uC5C5\xB7\uD64D\uBCF4\uC6A9 \uC0AC\uC6A9 \uC704\uCE58\uB098 \uCD94\uAC00\uAE08\uC744 \uC124\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694."
      });
    } else if (scope.channels.length === 0 && !scope.channelAddons?.length) {
      ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        path: [
          "channels"
        ],
        message: "\uC0AC\uC6A9\uCC98\uB97C 1\uAC1C \uC774\uC0C1 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694."
      });
    }
    const all = [
      ...scope.channels,
      ...(scope.channelAddons ?? []).map((item) => item.key)
    ];
    if (new Set(all).size !== all.length) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "channelAddons"
      ],
      message: "\uAC19\uC740 \uC0AC\uC6A9\uCC98\uB97C \uAE30\uBCF8\uAC00\uC640 \uCD94\uAC00\uAE08\uC5D0 \uC911\uBCF5 \uC124\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694."
    });
    if (scope.channelAddons?.some((item) => item.key === UsageChannel2.PRODUCT_NAME)) ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "channelAddons"
      ],
      message: "\uC0C1\uD488\uBA85 \uC0AC\uC6A9\uC740 \uC774\uB984/\uBE0C\uB79C\uB4DC \uC0AC\uC6A9\uAD8C\uC5D0\uC11C \uC124\uC815\uD574 \uC8FC\uC138\uC694."
    });
  }
  if (isMediaUsageScope(scope) && new Set(scope.channels).size !== scope.channels.length) ctx.addIssue({
    code: z7.ZodIssueCode.custom,
    path: [
      "channels"
    ],
    message: "\uC0AC\uC6A9\uCC98\uAC00 \uC911\uBCF5\uB429\uB2C8\uB2E4."
  });
  if (isMediaUsageScope(scope) && scope.channels.includes(UsageChannel2.PRODUCT_NAME)) ctx.addIssue({
    code: z7.ZodIssueCode.custom,
    path: [
      "channels"
    ],
    message: "\uC0C1\uD488\uBA85 \uC0AC\uC6A9\uC740 \uC774\uB984/\uBE0C\uB79C\uB4DC \uC0AC\uC6A9\uAD8C\uC5D0\uC11C \uC124\uC815\uD574 \uC8FC\uC138\uC694."
  });
});
var forbiddenIndustriesInputSchema = z7.array(z7.string().transform(normalizeForbiddenIndustry).pipe(z7.string().min(1, "\uAE08\uC9C0 \uC5C5\uC885\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694.").max(MAX_FORBIDDEN_INDUSTRY_LENGTH, "\uAE08\uC9C0 \uC5C5\uC885\uC740 40\uC790 \uC774\uB0B4\uB85C \uC785\uB825\uD574 \uC8FC\uC138\uC694."))).max(Object.keys(FORBIDDEN_INDUSTRY_LABELS).length + MAX_CUSTOM_FORBIDDEN_INDUSTRIES).superRefine((items, ctx) => {
  if (new Set(items.map(forbiddenIndustryKey)).size !== items.length) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      message: "\uC774\uBBF8 \uCD94\uAC00\uD55C \uAE08\uC9C0 \uC5C5\uC885\uC774\uC5D0\uC694."
    });
  }
  if (items.filter((item) => !isPresetForbiddenIndustry(item)).length > MAX_CUSTOM_FORBIDDEN_INDUSTRIES) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      message: "\uC9C1\uC811 \uCD94\uAC00\uD558\uB294 \uAE08\uC9C0 \uC5C5\uC885\uC740 \uCD5C\uB300 10\uAC1C\uC608\uC694."
    });
  }
});
var listingOfferInputSchema = z7.object({
  /** 수정 시 기존 오퍼 매칭용 — 있으면 in-place 갱신, 없으면 신규(판매 중 리스팅이면 재심사) */
  id: z7.string().uuid().optional(),
  rightType: z7.nativeEnum(IpRightType4),
  /** 판매 기간 옵션 — 기간마다 가격 상이 (복수) */
  durationOptions: z7.array(offerDurationOptionSchema).min(1).max(4),
  region: z7.nativeEnum(UsageRegion),
  exclusivity: z7.nativeEnum(Exclusivity),
  modificationRights: z7.array(z7.nativeEnum(ModificationRight)).default([]),
  requiresPreApproval: z7.boolean().default(false),
  forbiddenIndustries: forbiddenIndustriesInputSchema.default([]),
  pricingModel: z7.nativeEnum(PricingModel),
  /** 매출 로열티 % (0.01% 단위 basis point, 100 = 1%) */
  royaltyRateBp: z7.number().int().positive().max(1e4).nullish(),
  /** 로열티 지급일 — 매달 N일까지 전월 매출 보고 + 로열티 입금 (29~31일은 없는 달이 있어 제외) */
  royaltyPaymentDay: z7.number().int().min(1).max(28).nullish(),
  violationPolicies: z7.array(z7.nativeEnum(ViolationPolicy)).default([]),
  /** 제공 범위·옵션 가격 — 이름/브랜드 사용권 전용 (다른 권리는 null) */
  brandScope: brandScopeSchema.nullish(),
  usageScope: usageScopeSchema.nullish()
}).superRefine((offer, ctx) => {
  const config = RIGHT_TYPE_FORM_CONFIG[offer.rightType];
  if (offer.rightType !== IpRightType4.COLLABORATION && !offer.usageScope) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "usageScope"
      ],
      message: "\uAD8C\uB9AC\uC758 \uC138\uBD80 \uC870\uAC74\uC744 \uC124\uC815\uD574 \uC8FC\uC138\uC694."
    });
  }
  if (offer.modificationRights.some((right) => !config.modification || !config.modificationOptions.includes(right)) || new Set(offer.modificationRights).size !== offer.modificationRights.length) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "modificationRights"
      ],
      message: "\uC774 \uAD8C\uB9AC\uC5D0\uC11C \uD5C8\uC6A9\uD558\uB294 \uC218\uC815 \uD56D\uBAA9\uB9CC \uC911\uBCF5 \uC5C6\uC774 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694."
    });
  }
  if (!config.exclusivity && offer.exclusivity !== Exclusivity.NON_EXCLUSIVE) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "exclusivity"
      ],
      message: "\uCD9C\uC5F0\uAD8C\uC5D0\uB294 \uB3C5\uC810 \uC870\uAC74\uC744 \uC124\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694."
    });
  }
  if (offer.usageScope?.kind === IpRightType4.APPEARANCE && offer.region === UsageRegion.DOMESTIC) {
    const scope = offer.usageScope;
    const sessions = scope.variants ?? [
      scope
    ];
    sessions.forEach((session, index) => {
      if (session.mode === "ONSITE" && !session.serviceArea.trim()) {
        ctx.addIssue({
          code: z7.ZodIssueCode.custom,
          path: scope.variants ? [
            "usageScope",
            "variants",
            index,
            "serviceArea"
          ] : [
            "usageScope",
            "serviceArea"
          ],
          message: "\uBC29\uBB38 \uAC00\uB2A5\uD55C \uC9C0\uC5ED\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694."
        });
      }
    });
  }
  if (offer.usageScope?.kind === IpRightType4.APPEARANCE && offer.usageScope.variants && // 협의(가격 없음)는 앱이 칸을 빼고 보낸다 — 없음과 null 을 같게 본다
  (offer.durationOptions[0]?.fixedAmount ?? null) !== appearanceMinimumPrice(offer.usageScope.variants)) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "durationOptions"
      ],
      message: "\uCD9C\uC5F0 \uBC29\uC2DD\uBCC4 \uAC00\uACA9\uACFC \uB300\uD45C \uAC00\uACA9\uC774 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC544\uC694."
    });
  }
  if (offer.usageScope?.kind === IpRightType4.APPEARANCE && !offer.usageScope.variants && offer.usageScope.onsitePackages && (offer.durationOptions[0]?.fixedAmount ?? null) !== appearancePackageMinimum2(offer.usageScope.onsitePackages)) ctx.addIssue({
    code: z7.ZodIssueCode.custom,
    path: [
      "durationOptions"
    ],
    message: "\uD604\uC7A5 \uBC29\uBB38 \uAD6C\uC131 \uAC00\uACA9\uACFC \uB300\uD45C \uAC00\uACA9\uC774 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC544\uC694."
  });
  if (offer.usageScope?.kind === IpRightType4.APPEARANCE && (offer.pricingModel !== PricingModel.FIXED || offer.durationOptions.length !== 1 || offer.durationOptions[0]?.months !== 0 || offer.royaltyRateBp != null || offer.royaltyPaymentDay != null)) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "durationOptions"
      ],
      message: "\uCD9C\uC5F0\uAD8C\uC740 \uC0AC\uC6A9 \uAE30\uAC04 \uC5C6\uC774 1\uD68C \uAE30\uC900 \uACE0\uC815\uAC00\uB85C \uC124\uC815\uD574 \uC8FC\uC138\uC694."
    });
  }
  if (offer.rightType === IpRightType4.COLLABORATION && !offer.id) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "rightType"
      ],
      message: "\uCF5C\uB77C\uBCF4 \uC0C1\uD488\uAD8C\uC740 \uC2E0\uADDC \uB4F1\uB85D\uD560 \uC218 \uC5C6\uC5B4\uC694."
    });
  }
  if (offer.usageScope?.kind === IpRightType4.CO_BRANDING && offer.usageScope.campaignPricing === "PER_CAMPAIGN" && offer.usageScope.campaignCount !== 1) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "usageScope",
        "campaignCount"
      ],
      message: "\uACF5\uB3D9\uBE0C\uB79C\uB529 \uAC00\uACA9\uC740 \uBE0C\uB79C\uB4DC \uC218 \uC81C\uD55C \uC5C6\uC774 \uCEA0\uD398\uC778 1\uAC74 \uAE30\uC900\uC73C\uB85C \uC124\uC815\uD574 \uC8FC\uC138\uC694."
    });
  }
  if ((offer.usageScope?.kind === IpRightType4.MERCHANDISE || offer.usageScope?.kind === IpRightType4.CO_BRANDING) && offer.usageScope.adultUsage && offer.usageScope.adultUsage.addonAmount < 1e3) ctx.addIssue({
    code: z7.ZodIssueCode.custom,
    path: [
      "usageScope",
      "adultUsage"
    ],
    message: "\uC131\uC778 \uC5C5\uC885 \uCD94\uAC00\uAE08\uC744 1,000\uC6D0 \uC774\uC0C1 \uC785\uB825\uD558\uAC70\uB098 \uAE30\uBCF8\uAC00 \uD3EC\uD568\uC73C\uB85C \uBC14\uAFD4 \uC8FC\uC138\uC694."
  });
  if ((offer.usageScope?.kind === IpRightType4.MERCHANDISE || offer.usageScope?.kind === IpRightType4.CO_BRANDING) && offer.usageScope.adultUsage && isAdultIndustryForbidden(offer.forbiddenIndustries)) ctx.addIssue({
    code: z7.ZodIssueCode.custom,
    path: [
      "usageScope",
      "adultUsage"
    ],
    message: "\uC131\uC778 \uC5C5\uC885\uC744 \uAE08\uC9C0\uD558\uBA74 \uC131\uC778 \uC0AC\uC6A9 \uC635\uC158\uC744 \uC124\uC815\uD560 \uC218 \uC5C6\uC5B4\uC694."
  });
  if (isPersonalUsage(offer.usageScope) && (offer.pricingModel !== PricingModel.FIXED || offer.royaltyRateBp != null || offer.royaltyPaymentDay != null)) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "pricingModel"
      ],
      message: "\uAC1C\uC778 \uAC10\uC0C1\uC6A9\uC740 \uB9E4\uCD9C \uB85C\uC5F4\uD2F0 \uC5C6\uC774 \uACE0\uC815\uAC00 \uB610\uB294 \uAC00\uACA9 \uD611\uC758\uB85C \uD310\uB9E4\uD574 \uC8FC\uC138\uC694."
    });
  }
  if (isGiveawayOnly(offer.usageScope) && (offer.pricingModel !== PricingModel.FIXED || offer.royaltyRateBp != null || offer.royaltyPaymentDay != null)) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "pricingModel"
      ],
      message: "\uBE44\uB9E4\uD488 \uC804\uC6A9 \uAD7F\uC988\uB294 \uB9E4\uCD9C \uB85C\uC5F4\uD2F0 \uC5C6\uC774 \uACE0\uC815\uAC00 \uB610\uB294 \uAC00\uACA9 \uD611\uC758\uB85C \uD310\uB9E4\uD574 \uC8FC\uC138\uC694."
    });
  }
  if (offer.usageScope?.kind === IpRightType4.CONTENT_SECONDARY) {
    const scope = offer.usageScope;
    if (scope.channels.includes(UsageChannel2.PACKAGING) || scope.channelAddons?.some((item) => item.key === UsageChannel2.PACKAGING)) {
      ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        path: [
          "usageScope",
          "channels"
        ],
        message: "\uC601\uC0C1 \uC0AC\uC6A9\uAD8C\uC5D0\uC11C\uB294 \uC0C1\uD488 \uD328\uD0A4\uC9C0 \uD45C\uAE30\uB97C \uC81C\uACF5\uD560 \uC218 \uC5C6\uC5B4\uC694. \uAE30\uC874 \uD328\uD0A4\uC9C0 \uC124\uC815\uC744 \uC81C\uAC70\uD574 \uC8FC\uC138\uC694."
      });
    }
  }
  if (offer.usageScope && offer.usageScope.kind !== offer.rightType) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "usageScope"
      ],
      message: "\uAD8C\uB9AC \uC885\uB958\uC640 \uC81C\uACF5 \uBC94\uC704\uAC00 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."
    });
  }
  if (offer.rightType === IpRightType4.NAME_BRAND && !offer.brandScope) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "brandScope"
      ],
      message: "\uC774\uB984/\uBE0C\uB79C\uB4DC \uC0AC\uC6A9\uAD8C\uC740 \uC81C\uACF5 \uBC94\uC704 \uC124\uC815\uC774 \uD544\uC694\uD569\uB2C8\uB2E4"
    });
  }
  if (offer.rightType !== IpRightType4.NAME_BRAND && offer.brandScope) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "brandScope"
      ],
      message: "\uC81C\uACF5 \uBC94\uC704\uB294 \uC774\uB984/\uBE0C\uB79C\uB4DC \uC0AC\uC6A9\uAD8C\uC5D0\uC11C\uB9CC \uC124\uC815\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4"
    });
  }
  const needsRoyalty = offer.pricingModel === PricingModel.REVENUE_SHARE || offer.pricingModel === PricingModel.FIXED_PLUS_ROYALTY;
  const months = offer.durationOptions.map((d) => d.months);
  if (new Set(months).size !== months.length) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "durationOptions"
      ],
      message: "\uAE30\uAC04\uC774 \uC911\uBCF5\uB429\uB2C8\uB2E4"
    });
  }
  if (months.includes(0) && months.length > 1) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "durationOptions"
      ],
      message: "\uAE30\uAC04 \uC5C6\uC74C(0) \uC635\uC158\uC740 \uB2E8\uB3C5\uC73C\uB85C\uB9CC \uC4F8 \uC218 \uC788\uC2B5\uB2C8\uB2E4"
    });
  }
  if (needsRoyalty && !offer.royaltyRateBp) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "royaltyRateBp"
      ],
      message: "\uB85C\uC5F4\uD2F0 %\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4"
    });
  }
  if (needsRoyalty && !offer.royaltyPaymentDay) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: [
        "royaltyPaymentDay"
      ],
      message: "\uB85C\uC5F4\uD2F0 \uC9C0\uAE09\uC77C(\uB9E4\uB2EC N\uC77C)\uC774 \uD544\uC694\uD569\uB2C8\uB2E4"
    });
  }
});
var listingOfferResponseSchema = z7.object({
  id: z7.string().uuid(),
  /** 오퍼 단위 심사 상태 — 공개 API 는 APPROVED 만 반환, 판매자 본인 조회에서만 PENDING 노출 */
  status: z7.nativeEnum(ListingOfferStatus),
  rightType: z7.nativeEnum(IpRightType4),
  durationOptions: z7.array(z7.object({
    months: z7.number().int(),
    fixedAmount: z7.number().int().nullable()
  })),
  region: z7.nativeEnum(UsageRegion),
  exclusivity: z7.nativeEnum(Exclusivity),
  modificationRights: z7.array(z7.nativeEnum(ModificationRight)),
  requiresPreApproval: z7.boolean(),
  forbiddenIndustries: z7.array(z7.string()),
  pricingModel: z7.nativeEnum(PricingModel),
  royaltyRateBp: z7.number().int().nullable(),
  /** 로열티 지급일 — 매달 N일까지 전월 매출 보고 + 로열티 입금 */
  royaltyPaymentDay: z7.number().int().nullable(),
  violationPolicies: z7.array(z7.nativeEnum(ViolationPolicy)),
  /** 제공 범위·옵션 가격 — 이름/브랜드 사용권 외에는 null */
  brandScope: brandScopeSchema.nullable(),
  usageScope: usageScopeSchema.nullish(),
  /** [세부 조건 보기] 에 그릴 줄 — 서버가 shared offerTermsRows 로 만든다 (앱용, 웹은 같은 함수로 직접 그린다) */
  termRows: offerTermsRowsSchema.optional(),
  /**
  * 협의로 계약하는 권리의 제안 입력 칸 (앱용) — 출연권은 협의 방식마다 하나, 협의가 없는 권리는 빈 배열.
  * 기간 칩의 "가격 협의"로 여는 기간은 앱이 initialMonths 대신 고른 기간을 쓴다
  */
  proposalForms: z7.array(proposalFormSpecSchema).optional(),
  /** 구매 화면의 제공 범위·추가 옵션 (앱용) — 웹은 같은 함수로 직접 그린다 */
  purchaseOptions: offerPurchaseOptionsSchema.optional()
});
var proposalPricingSchema = z7.object({
  baseAmount: z7.number().int().min(0).max(1e9),
  addonAmount: z7.number().int().min(0),
  addonMonths: z7.number().int().min(1).max(12).optional(),
  campaignCount: z7.number().int().min(1).max(100),
  addons: purchaseAddonsSchema,
  source: z7.object({
    brandScope: brandScopeSchema.nullish(),
    usageScope: usageScopeSchema.nullish(),
    forbiddenIndustries: z7.array(z7.string()),
    durationMonths: z7.array(offerDurationSchema)
  })
});
var contractTermsSchema = z7.object({
  proposalPricing: proposalPricingSchema.optional(),
  rightType: z7.nativeEnum(IpRightType4),
  durationMonths: z7.number().int(),
  region: z7.nativeEnum(UsageRegion),
  exclusivity: z7.nativeEnum(Exclusivity),
  modificationRights: z7.array(z7.nativeEnum(ModificationRight)),
  requiresPreApproval: z7.boolean(),
  forbiddenIndustries: z7.array(z7.string()),
  pricingModel: z7.nativeEnum(PricingModel),
  upfrontAmount: z7.number().int(),
  royaltyRateBp: z7.number().int().nullable(),
  /** 로열티 지급일 — 매달 N일까지 전월 매출 보고 + 로열티 입금 (지급일 도입 전 계약은 없음) */
  royaltyPaymentDay: z7.number().int().nullish(),
  violationPolicies: z7.array(z7.nativeEnum(ViolationPolicy)),
  /**
  * 계약에 포함된 제공 범위 — 기본 포함 항목 + 구매자가 선택한 가산 옵션만 담는다
  * (오퍼의 brandScope 와 달리 "이 계약에서 쓸 수 있는 것" 목록)
  */
  brandScope: brandScopeSchema.nullish(),
  usageScope: usageScopeSchema.nullish(),
  /** 계약 시점의 판매자 주의·규칙 사항 (listing 에서 동결) */
  usageNotes: z7.string().nullable().optional()
});
var LISTING_THUMBNAIL_WIDTH = 705;
var LISTING_THUMBNAIL_HEIGHT = 396;
var LISTING_THUMBNAIL_MAX_BYTES = 2 * 1024 * 1024;
var LISTING_THUMBNAIL_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp"
];
var listingThumbnailInputSchema = z7.object({
  mimeType: z7.enum(LISTING_THUMBNAIL_MIME_TYPES),
  /** 이미지 바이트의 base64 (data: 접두어 없이) */
  data: z7.string().min(1).max(Math.ceil(LISTING_THUMBNAIL_MAX_BYTES / 3) * 4, "\uC774\uBBF8\uC9C0\uB294 2MB \uC774\uD558\uB85C \uC62C\uB824 \uC8FC\uC138\uC694").regex(/^[A-Za-z0-9+/]+={0,2}$/, "\uC378\uB124\uC77C \uC774\uBBF8\uC9C0 \uD615\uC2DD\uC774 \uC62C\uBC14\uB974\uC9C0 \uC54A\uC544\uC694")
});
function listingThumbnailPath(listingId, updatedAt) {
  return updatedAt ? `/listings/${listingId}/thumbnail?v=${updatedAt.getTime()}` : null;
}
__name(listingThumbnailPath, "listingThumbnailPath");
var listingSummarySchema = z7.object({
  id: z7.string().uuid(),
  title: z7.string(),
  sellerId: z7.string().uuid(),
  sellerName: z7.string(),
  /** 대표가 — 오퍼 중 최저 고정가. 로열티-only 면 0 (프론트는 "협의" 표기) */
  priceAmount: z7.number().int(),
  priceCurrency: z7.string(),
  /** 판매 중인 권리 종류 (중복 제거) */
  rightTypes: z7.array(z7.nativeEnum(IpRightType4)),
  status: z7.nativeEnum(ListingStatus),
  publishedAt: z7.string().datetime().nullable(),
  /** 썸네일 — 없으면 null (이모지 표지). listingThumbnailPath */
  thumbnailPath: z7.string().nullable()
});
var listingPageSchema = pageResponseSchema(listingSummarySchema);
var listMyListingScrapsQuerySchema = pageQuerySchema.pick({
  page: true
});
var listingDetailSchema = listingSummarySchema.extend({
  description: z7.string(),
  /** 기타 주의·규칙 사항 */
  usageNotes: z7.string().nullable(),
  offers: z7.array(listingOfferResponseSchema)
});
var createListingRequestSchema = z7.object({
  title: z7.string().min(2).max(200),
  description: z7.string().min(10).max(5e3),
  /** 기타 주의·규칙 사항 (선택) */
  usageNotes: noteItemsTextSchema(3e3).optional(),
  /** 썸네일 (선택) — 상품과 함께 저장한다. 지우기는 DELETE /listings/:id/thumbnail */
  thumbnail: listingThumbnailInputSchema.optional(),
  // 문서에서 응답 모양 그대로 — 앱은 폼 판정이 준 조건을 같은 모델로 보낸다
  offers: z7.array(sentAsReceived(listingOfferInputSchema)).min(1).max(20).superRefine((offers, ctx) => {
    if (new Set(offers.map((offer) => offer.rightType)).size !== offers.length) {
      ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        message: "\uD55C \uC0C1\uD488\uC5D0\uB294 \uAD8C\uB9AC\uB9C8\uB2E4 \uC624\uD37C\uB97C \uD558\uB098\uB9CC \uB458 \uC218 \uC788\uC5B4\uC694."
      });
    }
    const purposes = offers.filter((offer) => supportsUsagePurpose(offer.rightType)).map((offer) => getUsagePurpose(offer.usageScope) ?? UsagePurpose.BUSINESS);
    if (new Set(purposes).size > 1) {
      ctx.addIssue({
        code: z7.ZodIssueCode.custom,
        message: "\uC0AC\uC9C4\xB7\uC601\uC0C1\xB7\uC74C\uC131\uC758 \uC774\uC6A9 \uBAA9\uC801\uC740 \uD55C \uC0C1\uD488\uC5D0\uC11C \uB3D9\uC77C\uD574\uC57C \uD574\uC694. \uAC1C\uC778 \uAC10\uC0C1\uC6A9\uACFC \uC0AC\uC5C5\xB7\uD64D\uBCF4\uC6A9\uC740 \uBCC4\uB3C4 \uC0C1\uD488\uC73C\uB85C \uB4F1\uB85D\uD574 \uC8FC\uC138\uC694."
      });
    }
  })
});
var updateListingRequestSchema = createListingRequestSchema;
var myListingSchema = listingSummarySchema.extend({
  description: z7.string(),
  usageNotes: z7.string().nullable(),
  offers: z7.array(listingOfferResponseSchema),
  /** 진행 중인 거래(협의·입금 대기·발급 전)가 걸려 조건을 바꾸거나 내릴 수 없는 권리 */
  lockedOfferIds: z7.array(z7.string().uuid()),
  /** 어드민 판매 중지 사유 (중지 상태일 때만) */
  suspendedReason: z7.string().nullable(),
  createdAt: z7.string().datetime()
});
var scrapStateSchema = z7.object({
  scrapped: z7.boolean()
});

// src/user/license-draft-form.schema.ts
import { z as z8 } from "zod";
var licenseDraftFormSpecSchema = z8.object({
  startLabel: z8.string(),
  startMaxDaysAhead: z8.number().int(),
  appearance: z8.boolean(),
  specialTermsLabel: z8.string(),
  required: z8.boolean(),
  placeholders: z8.array(z8.string()),
  guideTitle: z8.string(),
  guidePreface: z8.string().nullable(),
  guideBody: z8.string(),
  hints: z8.array(z8.string()),
  missingHint: z8.string()
});

// src/user/orders.schema.ts
import { BrandAsset as BrandAsset3, DepositReturnKind, DepositReturnStatus, DisputeResolution, ViolationReportResolution, AppearanceMode as AppearanceMode4, BrandLegalUse as BrandLegalUse3, FulfillmentType, DEPOSITOR_NAME_MAX_LENGTH, IpRightType as IpRightType5, isValidDateOnly, LicenseAmendmentStatus, LicenseStatus, OrderIssue, OrderStatus, PaymentMethod, PaymentStatus, RefundStatus, TerminationKind, TerminationRefundMode, UsagePlacement as UsagePlacement3, UsageChannel as UsageChannel3, MerchandiseChannel as MerchandiseChannel3 } from "@inflip/shared";
import { z as z9 } from "zod";
var createOrderItemSchema = z9.object({
  /** 구매할 오퍼 — 조건/가격은 주문 시점에 스냅샷으로 동결된다 */
  offerId: z9.string().uuid(),
  /** 오퍼가 파는 기간 옵션 중 선택 */
  durationMonths: z9.number().int(),
  /** 공동브랜딩 1건당 가격 상품에서 구매할 캠페인 수 */
  campaignCount: z9.number().int().min(1).max(100).optional(),
  /**
  * 선택한 가산 옵션 — brandScope 또는 usageScope.channelAddons의 유료 항목만 지정 가능.
  * 기본가 포함 항목은 보내지 않아도 계약에 자동 포함된다.
  */
  addons: z9.object({
    appearanceMode: z9.nativeEnum(AppearanceMode4).optional(),
    onsitePackageIds: z9.array(z9.string().min(1).max(80)).min(1).max(10).optional(),
    assets: z9.array(z9.nativeEnum(BrandAsset3)).max(5).default([]),
    placements: z9.array(z9.nativeEnum(UsagePlacement3)).max(Object.keys(UsagePlacement3).length).default([]),
    legalUses: z9.array(z9.nativeEnum(BrandLegalUse3)).max(3).default([]),
    channels: z9.array(z9.nativeEnum(UsageChannel3)).max(7).optional(),
    merchandiseChannels: z9.array(z9.nativeEnum(MerchandiseChannel3)).max(3).optional(),
    adultUsage: z9.boolean().optional()
  }).optional()
});
var createOrderRequestSchema = z9.object({
  listingId: z9.string().uuid(),
  items: z9.array(createOrderItemSchema).min(1).max(10).refine((items) => new Set(items.map((i) => i.offerId)).size === items.length, "\uAC19\uC740 \uC624\uD37C\uB97C \uC911\uBCF5 \uC120\uD0DD\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4"),
  /**
  * 구매자가 화면에서 본 합계 — 서버가 다시 계산한 금액과 다르면(그 사이 판매자가 가격을 바꿨거나
  * 화면이 오래된 경우) 주문을 만들지 않고 PRICE_CHANGED 로 알린다. 없으면 검사하지 않는다(구버전 호환)
  */
  expectedTotal: z9.number().int().nonnegative().optional()
});
var paymentResponseSchema = z9.object({
  id: z9.string().uuid(),
  method: z9.nativeEnum(PaymentMethod),
  status: z9.nativeEnum(PaymentStatus),
  amount: z9.number().int(),
  currency: z9.string(),
  /** 무통장 전용 — 입금자명 (기본 구매자 닉네임, 입금 대기 중 변경 가능) */
  depositReference: z9.string().nullable(),
  depositDueAt: z9.string().datetime().nullable()
});
var updateDepositorNameRequestSchema = z9.object({
  depositorName: z9.string().trim().min(1, "\uC785\uAE08\uC790\uBA85\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(DEPOSITOR_NAME_MAX_LENGTH, `\uC785\uAE08\uC790\uBA85\uC740 ${DEPOSITOR_NAME_MAX_LENGTH}\uC790\uAE4C\uC9C0 \uC785\uB825\uD560 \uC218 \uC788\uC5B4\uC694`)
});
var updateDepositorNameResponseSchema = z9.object({
  depositReference: z9.string()
});
var addonPaymentSchema = z9.object({
  id: z9.string().uuid(),
  status: z9.nativeEnum(PaymentStatus),
  amount: z9.number().int(),
  /** 추가금 사유 — 판매자가 적고 구매자 화면에 그대로 보인다 */
  memo: z9.string().nullable(),
  depositReference: z9.string().nullable(),
  depositDueAt: z9.string().datetime().nullable(),
  confirmedAt: z9.string().datetime().nullable(),
  createdAt: z9.string().datetime()
});
var requestAddonPaymentSchema = z9.object({
  amount: z9.number().int().positive().max(1e8),
  /** 추가금 사유 — 무엇이 합의로 늘었는지 구매자가 알 수 있게 */
  memo: z9.string().trim().min(2).max(300)
});
var requestRefundRequestSchema = z9.object({
  reason: z9.string().min(5).max(500),
  /** 환불금을 돌려받을 본인 계좌 (무통장 환불) */
  bankAccount: z9.object({
    bank: z9.string().min(1).max(50),
    accountNumber: z9.string().min(4).max(50),
    holder: z9.string().min(1).max(100)
  })
});
var orderRefundSummarySchema = z9.object({
  id: z9.string().uuid(),
  status: z9.nativeEnum(RefundStatus),
  amount: z9.number().int(),
  reason: z9.string(),
  rejectReason: z9.string().nullable(),
  /** 계약 해지와 함께 만든 환불 — 상태는 해지 안내가 함께 설명하므로 따로 안내 박스를 띄우지 않는다 */
  fromTermination: z9.boolean(),
  createdAt: z9.string().datetime()
});
var orderViolationReportSummarySchema = z9.object({
  id: z9.string().uuid(),
  reason: z9.string(),
  evidenceCount: z9.number().int(),
  /** null 이면 관리자 확인 중 */
  resolution: z9.nativeEnum(ViolationReportResolution).nullable(),
  resolutionNote: z9.string().nullable(),
  resolvedAt: z9.string().datetime().nullable(),
  createdAt: z9.string().datetime()
});
var orderDepositReturnSchema = z9.object({
  id: z9.string().uuid(),
  kind: z9.nativeEnum(DepositReturnKind),
  status: z9.nativeEnum(DepositReturnStatus),
  receivedAmount: z9.number().int(),
  amount: z9.number().int(),
  reason: z9.string(),
  /** 돌려받을 계좌 — 은행·뒤 4자리만 */
  bankLabel: z9.string(),
  completedAt: z9.string().datetime().nullable(),
  createdAt: z9.string().datetime()
});
var orderTerminationSummarySchema = z9.object({
  kind: z9.nativeEnum(TerminationKind),
  reason: z9.string(),
  refundMode: z9.nativeEnum(TerminationRefundMode),
  refundAmount: z9.number().int(),
  /** 해지와 함께 돌려주는 선불의 환불 상태 — 비환불이면 null */
  refundStatus: z9.nativeEnum(RefundStatus).nullable(),
  executedAt: z9.string().datetime().nullable()
});
var orderGroupMemberSchema = z9.object({
  id: z9.string().uuid(),
  rightType: z9.nativeEnum(IpRightType5),
  /** 계약 금액 — 묶음 입금에 들어간 금액. 묶음의 contractAmount 합 = 묶음 합계 */
  contractAmount: z9.number().int(),
  totalAmount: z9.number().int(),
  status: z9.nativeEnum(OrderStatus)
});
var orderResponseSchema = z9.object({
  id: z9.string().uuid(),
  listingId: z9.string().uuid(),
  listingTitle: z9.string().optional(),
  /** 상품 썸네일 — 목록 표지. 없으면 null (이모지 표지), 제목처럼 목록·상세에만 실린다 */
  listingThumbnailPath: z9.string().nullish(),
  status: z9.nativeEnum(OrderStatus),
  rightType: z9.nativeEnum(IpRightType5),
  /** 계약 조건 스냅샷 (주문 시점 동결, 기간 확정) */
  terms: contractTermsSchema.nullable(),
  /** 계약 조건을 그릴 줄 — 서버가 shared offerTermsRows 로 만든다 (앱용). 조건이 없으면 null */
  termRows: offerTermsRowsSchema.nullish(),
  fulfillmentType: z9.nativeEnum(FulfillmentType),
  /** 계약 금액 — 주문(협의 수락) 때 정한 선불 금액. 바뀌지 않는다 */
  contractAmount: z9.number().int(),
  /** 확인된 추가금 합 */
  addonAmount: z9.number().int(),
  /** 결제 총액 = 계약 금액 + 확인된 추가금. 환불 한도·정산 기준 */
  totalAmount: z9.number().int(),
  /** 입금 대기 중인 추가금 — 목록 조회에서 채워진다. 없으면 null */
  pendingAddonAmount: z9.number().int().nullable().optional(),
  /** 처리 중인 일(분쟁·위반 신고·환불 등) — 목록 조회에서 채워진다 */
  issues: z9.array(z9.nativeEnum(OrderIssue)).optional(),
  currency: z9.string(),
  paidAt: z9.string().datetime().nullable(),
  /** 이 주문에 고정된 플랫폼 수수료(bp) — 주문 시점의 결제 설정 */
  platformFeeRateBp: z9.number().int().optional(),
  /** 라이선스 발급 시각 (로열티 보고 기간은 발급 시각이 아니라 licenseStartsAt 기준) */
  licenseIssuedAt: z9.string().datetime().nullable().optional(),
  /** 계약 시간대 (IANA) — 주문 시점 판매자 시간대로 고정. 계약 날짜는 이 시간대로 계산·표시 */
  timeZone: z9.string(),
  /** 계약 효력 시작·만료 — 로열티 보고 기간의 기준 (만료 null = 무기한) */
  licenseStartsAt: z9.string().datetime().nullable().optional(),
  licenseExpiresAt: z9.string().datetime().nullable().optional(),
  /** 자동 구매확정 기한 — 라이선스 발급 후 정해진다. 지나면 자동 확정되고 정산이 시작된다 */
  purchaseConfirmDueAt: z9.string().datetime().nullable().optional(),
  /** 구매확정 시각·주체 (USER = 구매자가 확정, SYSTEM = 기한 경과 자동 확정, ADMIN = 분쟁 기각·계약 해지로 관리자 확정) */
  purchaseConfirmedAt: z9.string().datetime().nullable().optional(),
  purchaseConfirmedBy: orderTransitionActorSchema.nullable().optional(),
  /** 분쟁 — 사유·접수 시각, 처리 결과(기각 = 정산 진행 / 전액 환불)·처리 사유·시각 */
  disputeReason: z9.string().nullable().optional(),
  disputedAt: z9.string().datetime().nullable().optional(),
  disputeResolution: z9.nativeEnum(DisputeResolution).nullable().optional(),
  disputeResolutionNote: z9.string().nullable().optional(),
  disputeResolvedAt: z9.string().datetime().nullable().optional(),
  /** 취소 주체 — SYSTEM 이면 입금 기한 만료(미입금 취소), USER/ADMIN 이면 주문 취소 */
  cancelledBy: orderTransitionActorSchema.nullable().optional(),
  cancelledAt: z9.string().datetime().nullable().optional(),
  cancelReason: z9.string().nullable().optional(),
  /** 만료 후 입금으로 되살린 시각 — 기한이 지나 취소됐다가 늦게 들어온 입금이 확인된 주문 */
  revivedAt: z9.string().datetime().nullable().optional(),
  /** 입금 반환 내역 — 구매자 상세 조회에서만 채워진다 */
  depositReturns: z9.array(orderDepositReturnSchema).optional(),
  /** 계약 해지 시각 = 마지막 사용일(계약 시간대 날짜)의 기준. 해지된 계약만 */
  terminatedAt: z9.string().datetime().nullable().optional(),
  /** 계약 해지 요약 — 목록·상세 조회에서 해지된 주문만 채워진다 */
  termination: orderTerminationSummarySchema.nullable().optional(),
  /** 현재(최신) 결제 — 입금 안내 표시용. 그룹 결제 주문이면 null (그룹에서 안내) */
  payment: paymentResponseSchema.nullable(),
  /** 결제 묶음 소속이면 그룹 id — 입금 안내는 /checkout/{orderGroupId} 에서 */
  orderGroupId: z9.string().uuid().nullable().optional(),
  /** 같은 묶음의 주문 전체(이 주문 포함, 주문순) — 상세 조회에서만 채워진다. 1건이면 묶음 아님 */
  groupOrders: z9.array(orderGroupMemberSchema).optional(),
  /** 환불 진행 내역 — 상세 조회에서만 채워진다 */
  refunds: z9.array(orderRefundSummarySchema).optional(),
  /** 판매자 위반 신고 — 최근 것부터 (상세 조회에서만) */
  violationReports: z9.array(orderViolationReportSummarySchema).optional(),
  /** 추가금 결제 내역 — 상세·묶음 입금 안내 조회에서 채워진다 */
  addonPayments: z9.array(addonPaymentSchema).optional(),
  createdAt: z9.string().datetime()
});
var orderGroupResponseSchema = z9.object({
  id: z9.string().uuid(),
  listingId: z9.string().uuid(),
  listingTitle: z9.string().optional(),
  totalAmount: z9.number().int(),
  currency: z9.string(),
  status: z9.enum([
    "PAYMENT_PENDING",
    "PAID_OR_LATER",
    "CANCELLED"
  ]),
  orders: z9.array(orderResponseSchema),
  /** 그룹 결제 (무통장 입금 안내 1건) */
  payment: paymentResponseSchema.nullable(),
  createdAt: z9.string().datetime()
});
var listingOfferStateSchema = z9.object({
  offerId: z9.string().uuid(),
  state: z9.enum([
    "AVAILABLE",
    "OWNED",
    "PENDING_ORDER"
  ]),
  /** OWNED 일 때 활성 라이선스 만료일 (무기한이면 null) */
  activeLicenseExpiresAt: z9.string().datetime().nullable().optional(),
  /** 그 라이선스 계약 시간대 — 만료일을 계약 시간대 날짜로 보여 준다 */
  activeLicenseTimeZone: z9.string().optional(),
  /** PENDING_ORDER 일 때 진행 중 주문 */
  orderId: z9.string().uuid().optional()
});
var sellerOrderResponseSchema = orderResponseSchema.extend({
  buyerId: z9.string().uuid().optional(),
  buyerName: z9.string().nullable().optional(),
  /** 발급 예정 내용 작성 칸 (판매자) */
  draftForm: licenseDraftFormSpecSchema.optional()
});
var issueLicenseRequestSchema = z9.object({
  draftUpdatedAt: z9.string().datetime()
});
var issueLicenseResponseSchema = z9.object({
  licenseId: z9.string().uuid(),
  licenseKey: z9.string()
});
var licenseDraftSchema = z9.object({
  startsAt: z9.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD \uD615\uC2DD").nullable(),
  specialTerms: z9.string().nullable(),
  updatedAt: z9.string().datetime(),
  /** 구매자가 이 내용을 확인한 시각 — null 이면 확인 전 (내용을 고치면 다시 null) */
  buyerConfirmedAt: z9.string().datetime().nullable()
});
var licenseDraftResponseSchema = z9.object({
  draft: licenseDraftSchema.nullable()
});
var upsertLicenseDraftRequestSchema = z9.object({
  startsAt: z9.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD \uD615\uC2DD").refine(isValidDateOnly, "\uC5C6\uB294 \uB0A0\uC9DC\uC608\uC694").optional(),
  specialTerms: noteItemsTextSchema(2e3).optional()
});
var confirmLicenseDraftRequestSchema = z9.object({
  seenUpdatedAt: z9.string().datetime()
});
var licenseAmendmentTermsSchema = z9.object({
  startsAt: z9.string().datetime(),
  expiresAt: z9.string().datetime().nullable(),
  specialTerms: z9.string().nullable(),
  /** 구매확정 기한 — 구매확정 전에만 시작일을 따라 옮긴다 */
  purchaseConfirmDueAt: z9.string().datetime().nullable()
});
var licenseAmendmentSchema = z9.object({
  id: z9.string().uuid(),
  status: z9.nativeEnum(LicenseAmendmentStatus),
  reason: z9.string(),
  /** 만료일을 직접 정했는지 — 아니면 계약 기간대로 시작일을 따라감 */
  expiresAtManual: z9.boolean(),
  before: licenseAmendmentTermsSchema,
  after: licenseAmendmentTermsSchema,
  buyerAgreedAt: z9.string().datetime().nullable(),
  sellerAgreedAt: z9.string().datetime().nullable(),
  appliedAt: z9.string().datetime().nullable(),
  declinedBy: z9.enum([
    "BUYER",
    "SELLER"
  ]).nullable(),
  declinedAt: z9.string().datetime().nullable(),
  declineReason: z9.string().nullable(),
  cancelledAt: z9.string().datetime().nullable(),
  createdAt: z9.string().datetime()
});
var licenseAmendmentListResponseSchema = z9.object({
  amendments: z9.array(licenseAmendmentSchema)
});
var licenseSuspensionSchema = z9.object({
  id: z9.string().uuid(),
  reason: z9.string(),
  suspendedAt: z9.string().datetime(),
  resumedAt: z9.string().datetime().nullable(),
  /** 재개 메모 (조사 결과 등) */
  resumeNote: z9.string().nullable()
});
var licenseSuspensionListResponseSchema = z9.object({
  suspensions: z9.array(licenseSuspensionSchema),
  /** 지금 라이선스 상태 — SUSPENDED 면 풀리지 않은 중지가 걸려 있다. 발급 전이면 null */
  licenseStatus: z9.nativeEnum(LicenseStatus).nullable()
});
var declineLicenseAmendmentRequestSchema = z9.object({
  reason: z9.string().trim().max(500).optional()
});
var cancelMyOrderRequestSchema = z9.object({
  reason: z9.string().max(500).optional()
});
var DISPUTE_EVIDENCE_MAX_FILES = 10;
var DISPUTE_EVIDENCE_MAX_BYTES = 10 * 1024 * 1024;
var DISPUTE_EVIDENCE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif"
];
var disputeEvidenceMimeSchema = z9.enum(DISPUTE_EVIDENCE_MIME_TYPES, {
  errorMap: /* @__PURE__ */ __name(() => ({
    message: "\uC774\uBBF8\uC9C0(JPG\xB7PNG\xB7WEBP\xB7GIF\xB7HEIC)\uB9CC \uCCA8\uBD80\uD560 \uC218 \uC788\uC5B4\uC694"
  }), "errorMap")
});
var disputeEvidenceFileShape = {
  filename: z9.string().min(1).max(255),
  mime: disputeEvidenceMimeSchema,
  sizeBytes: z9.number().int().positive().max(DISPUTE_EVIDENCE_MAX_BYTES, "\uC774\uBBF8\uC9C0 \uD55C \uC7A5\uC740 10MB\uAE4C\uC9C0 \uCCA8\uBD80\uD560 \uC218 \uC788\uC5B4\uC694")
};
var disputeEvidencePresignRequestSchema = z9.object({
  orderId: z9.string().uuid(),
  ...disputeEvidenceFileShape
});
var disputeEvidencePresignResponseSchema = z9.object({
  uploadUrl: z9.string().url(),
  key: z9.string(),
  headers: z9.record(z9.string()),
  expiresIn: z9.number().int().positive()
});
var disputeEvidenceSchema = z9.object({
  key: z9.string().min(1).max(500),
  ...disputeEvidenceFileShape
});
var disputeOrderRequestSchema = z9.object({
  reason: z9.string().min(10, "\uBD84\uC7C1 \uC0AC\uC720\uB97C 10\uC790 \uC774\uC0C1 \uC801\uC5B4\uC8FC\uC138\uC694").max(2e3),
  /** 증거 이미지 (선택, 최대 10장) */
  evidence: z9.array(disputeEvidenceSchema).max(DISPUTE_EVIDENCE_MAX_FILES, `\uC99D\uAC70 \uC774\uBBF8\uC9C0\uB294 ${DISPUTE_EVIDENCE_MAX_FILES}\uC7A5\uAE4C\uC9C0 \uCCA8\uBD80\uD560 \uC218 \uC788\uC5B4\uC694`).optional()
});
var reportViolationRequestSchema = z9.object({
  reason: z9.string().trim().min(10, "\uC2E0\uACE0 \uC0AC\uC720\uB97C 10\uC790 \uC774\uC0C1 \uC801\uC5B4\uC8FC\uC138\uC694").max(2e3),
  evidence: z9.array(disputeEvidenceSchema).max(DISPUTE_EVIDENCE_MAX_FILES, `\uC99D\uAC70 \uC774\uBBF8\uC9C0\uB294 ${DISPUTE_EVIDENCE_MAX_FILES}\uC7A5\uAE4C\uC9C0 \uCCA8\uBD80\uD560 \uC218 \uC788\uC5B4\uC694`).optional()
});
var ORDER_LIST_PAGE = 20;
var ORDER_STATUS_GROUPS = [
  {
    key: "awaiting-payment",
    label: "\uC785\uAE08 \uB300\uAE30",
    statuses: [
      OrderStatus.ORDER_CREATED,
      OrderStatus.PAYMENT_PENDING
    ]
  },
  {
    key: "awaiting-issue",
    label: "\uBC1C\uAE09 \uB300\uAE30",
    statuses: [
      OrderStatus.PAID
    ]
  },
  {
    key: "issued",
    label: "\uAC70\uB798 \uC644\uB8CC",
    statuses: [
      OrderStatus.LICENSE_ISSUED,
      OrderStatus.SETTLEMENT_PENDING,
      OrderStatus.PAYOUT_COMPLETED
    ]
  },
  {
    key: "closed",
    label: "\uCDE8\uC18C\xB7\uD658\uBD88\xB7\uD574\uC9C0",
    statuses: [
      OrderStatus.CANCELLED,
      OrderStatus.REFUNDED,
      OrderStatus.TERMINATED
    ]
  },
  {
    key: "disputed",
    label: "\uBD84\uC7C1",
    statuses: [
      OrderStatus.DISPUTED
    ]
  }
];
var orderStatusGroupKeySchema = z9.enum(ORDER_STATUS_GROUPS.map((g) => g.key));
var listMyOrdersQuerySchema = z9.object({
  group: orderStatusGroupKeySchema.optional(),
  before: z9.string().uuid().optional(),
  limit: z9.coerce.number().int().min(1).max(100).default(ORDER_LIST_PAGE)
});
var orderIssueRowSchema = z9.object({
  orderId: z9.string().uuid(),
  listingId: z9.string().uuid(),
  listingTitle: z9.string().nullable(),
  rightType: z9.nativeEnum(IpRightType5),
  status: z9.nativeEnum(OrderStatus),
  issues: z9.array(z9.nativeEnum(OrderIssue))
});
var orderSideSummarySchema = z9.object({
  total: z9.number().int(),
  /** 상태 칩별 수 */
  groups: z9.record(orderStatusGroupKeySchema, z9.number().int()),
  /** 가장 최근 거래 활동(주문·입금 확인·취소) 시각 — 어느 탭을 먼저 열지 정한다. 없으면 null */
  latestActivityAt: z9.string().datetime().nullable(),
  /** 처리 중인 일이 걸린 거래 — 최근 주문 순, 전체 기준 */
  issues: z9.array(orderIssueRowSchema)
});
var ordersSummarySchema = z9.object({
  buy: orderSideSummarySchema,
  sell: orderSideSummarySchema
});

// src/user/royalties.schema.ts
import { RoyaltyObjectionStatus, RoyaltyReportKind, RoyaltyReportStatus } from "@inflip/shared";
import { z as z10 } from "zod";
var createRoyaltyReportRequestSchema = z10.object({
  /** YYYY-MM */
  period: z10.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "YYYY-MM \uD615\uC2DD"),
  reportedRevenue: z10.number().int().min(0).max(1e12),
  note: z10.string().max(1e3).optional()
});
var updateRoyaltyReportRequestSchema = z10.object({
  reportedRevenue: z10.number().int().min(0).max(1e12),
  note: z10.string().max(1e3).optional()
});
var sellerVoidRoyaltyReportRequestSchema = z10.object({
  reason: z10.string().trim().min(5, "\uBC18\uB824 \uC0AC\uC720\uB97C 5\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(500)
});
var royaltyDepositNoticeRequestSchema = z10.object({
  reportIds: z10.array(z10.string().uuid()).min(1).max(100)
});
var royaltyDepositNoticeResponseSchema = z10.object({
  ok: z10.literal(true),
  notifiedAt: z10.string().datetime()
});
var royaltyReportFileSchema = z10.object({
  id: z10.string().uuid(),
  fileName: z10.string(),
  mimeType: z10.string(),
  size: z10.number().int(),
  uploaderRole: z10.enum([
    "BUYER",
    "SELLER"
  ]),
  objectionId: z10.string().uuid().nullable(),
  createdAt: z10.string().datetime()
});
var royaltyObjectionSchema = z10.object({
  id: z10.string().uuid(),
  reason: z10.string(),
  status: z10.nativeEnum(RoyaltyObjectionStatus),
  /** 어드민 처리 내용 (기각 사유·정정 근거) */
  resolution: z10.string().nullable(),
  createdAt: z10.string().datetime(),
  resolvedAt: z10.string().datetime().nullable()
});
var royaltyReportResponseSchema = z10.object({
  id: z10.string().uuid(),
  orderId: z10.string().uuid(),
  period: z10.string(),
  status: z10.nativeEnum(RoyaltyReportStatus),
  /** ORIGINAL 월 보고 / ADJUSTMENT 입금 확인 이후 정정분(금액은 차액, 음수면 환급·회수) */
  kind: z10.nativeEnum(RoyaltyReportKind),
  adjustsReportId: z10.string().uuid().nullable(),
  reportedRevenue: z10.number().int(),
  royaltyRateBp: z10.number().int(),
  royaltyAmount: z10.number().int(),
  sellerPayoutAmount: z10.number().int(),
  note: z10.string().nullable(),
  confirmedAt: z10.string().datetime().nullable(),
  paidOutAt: z10.string().datetime().nullable(),
  /** 어드민 반려 사유·시각 */
  voidReason: z10.string().nullable(),
  voidedAt: z10.string().datetime().nullable(),
  /** 구매자가 '입금했어요'로 알린 시각 (빠른 입금 확인 요청) */
  depositNotifiedAt: z10.string().datetime().nullable(),
  /** 반려한 쪽 — 어드민 / 판매자 */
  voidedByRole: z10.enum([
    "ADMIN",
    "SELLER"
  ]).nullable(),
  files: z10.array(royaltyReportFileSchema),
  objections: z10.array(royaltyObjectionSchema),
  createdAt: z10.string().datetime()
});
var royaltyTodoSchema = z10.object({
  kind: z10.enum([
    "REPORT",
    "DEPOSIT",
    "EVIDENCE_REQUESTED",
    "AWAITING_REPORT",
    "OBJECTION_OPEN"
  ]),
  period: z10.string(),
  /** 보고·입금 기한 (YYYY-MM-DD) */
  dueDate: z10.string().nullable(),
  overdue: z10.boolean(),
  /** DEPOSIT 이면 낼 금액 */
  amount: z10.number().int().nullable()
});
var royaltyContractSchema = z10.object({
  orderId: z10.string().uuid(),
  role: z10.enum([
    "BUYER",
    "SELLER"
  ]),
  /** 상대방 이름 — 구매자면 판매자(로열티를 받는 쪽), 판매자면 구매자(로열티를 내는 쪽) */
  counterpartyName: z10.string().nullable(),
  listingTitle: z10.string().nullable(),
  royaltyRateBp: z10.number().int(),
  royaltyPaymentDay: z10.number().int().nullable(),
  durationMonths: z10.number().int().nullable(),
  licenseIssuedAt: z10.string().datetime().nullable(),
  /** 계약 시간대 (IANA) — 주문 시점 판매자 시간대로 고정. 계약 날짜는 이 시간대로 계산·표시 */
  timeZone: z10.string(),
  /** 계약 효력 시작·만료 (만료 null = 무기한) */
  licenseStartsAt: z10.string().datetime().nullable(),
  licenseExpiresAt: z10.string().datetime().nullable(),
  /** 계약 해지 시각 — 해지된 계약만. 해지일이 있는 달이 마지막 보고 달 */
  terminatedAt: z10.string().datetime().nullable().optional(),
  /** 보고 기간 (첫 달 ~ 마지막 달, 무기한이면 null) */
  firstPeriod: z10.string().nullable(),
  lastPeriod: z10.string().nullable(),
  todos: z10.array(royaltyTodoSchema)
});
var myRoyaltiesResponseSchema = z10.object({
  contracts: z10.array(royaltyContractSchema),
  /** 내가 처리할 일 수 (구매자 할 일) — 마이페이지 메뉴 배지 */
  actionCount: z10.number().int()
});

// src/user/social-channels.schema.ts
import { MANUAL_SOCIAL_PLATFORMS, SocialChannelRequestStatus, SocialPlatform as SocialPlatform2 } from "@inflip/shared";
import { z as z11 } from "zod";
var VERIFIABLE_SOCIAL_PLATFORMS = [
  SocialPlatform2.YOUTUBE,
  SocialPlatform2.INSTAGRAM,
  SocialPlatform2.TIKTOK,
  SocialPlatform2.THREADS
];
var verifiableSocialPlatformSchema = z11.enum(VERIFIABLE_SOCIAL_PLATFORMS);
var manualSocialPlatformSchema = z11.enum(MANUAL_SOCIAL_PLATFORMS);
var socialPlatformSchema = z11.enum([
  ...VERIFIABLE_SOCIAL_PLATFORMS,
  // 스레드는 두 목록에 다 있다 (로그인 + 수동 요청)
  SocialPlatform2.SOOP,
  SocialPlatform2.PANDATV
]);
var socialChannelRequestStatusSchema = z11.enum([
  SocialChannelRequestStatus.PENDING,
  SocialChannelRequestStatus.APPROVED,
  SocialChannelRequestStatus.REJECTED
]);
var socialChannelSchema = z11.object({
  platform: socialPlatformSchema,
  /** 플랫폼이 준 계정/채널 ID */
  externalId: z11.string(),
  /** @handle / username — 없을 수도 있다 (유튜브 customUrl 미설정 등) */
  username: z11.string().nullable(),
  displayName: z11.string().nullable(),
  profileUrl: z11.string().url(),
  /** 인증 시점의 구독자·팔로워 수. 플랫폼이 숨기면 null */
  followerCount: z11.number().int().nonnegative().nullable(),
  verifiedAt: z11.string().datetime()
});
var mySocialChannelRequestSchema = z11.object({
  platform: manualSocialPlatformSchema,
  url: z11.string().url(),
  status: socialChannelRequestStatusSchema,
  /** 거절 시 어드민이 남긴 사유 */
  rejectReason: z11.string().nullable(),
  createdAt: z11.string().datetime(),
  processedAt: z11.string().datetime().nullable()
});
var socialChannelsResponseSchema = z11.object({
  channels: z11.array(socialChannelSchema),
  /**
  * 서버에 OAuth 앱 키가 설정된 플랫폼만 true — 아니면 화면에서 "준비 중".
  * 인스타그램은 로그인(OAuth)이 아니라 소개 코드라 늘 false — 옛 앱이 로그인 창을 열지 않게. 새 화면은 instagramBioCode 를 본다
  */
  available: z11.record(verifiableSocialPlatformSchema, z11.boolean()),
  /** 인스타그램 소개 코드 인증을 받는지 (서버에 조회용 계정이 설정됨) */
  instagramBioCode: z11.boolean(),
  /** 수동 인증 요청 현황 (스레드/SOOP/팬더TV) */
  requests: z11.array(mySocialChannelRequestSchema)
});
var createSocialChannelRequestSchema = z11.object({
  platform: manualSocialPlatformSchema,
  url: z11.string().trim().url("\uCC44\uB110 \uC8FC\uC18C \uC804\uCCB4\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694.").max(500)
});
var SOCIAL_CHANNEL_APP_SCHEMES = [
  "inflip",
  "inflip-dev",
  "inflip-local"
];
var socialChannelAuthorizeQuerySchema = z11.object({
  popupId: z11.string().uuid().optional(),
  appScheme: z11.enum(SOCIAL_CHANNEL_APP_SCHEMES).optional()
});
var socialChannelAuthorizeResponseSchema = z11.object({
  /** 브라우저가 이동할 플랫폼 로그인 URL */
  url: z11.string().url()
});
var TIKTOK_APP_REDIRECT_PATH = "/app/tiktok/callback";
var tiktokAppVerifyRequestSchema = z11.object({
  code: z11.string().trim().min(1).max(2e3),
  codeVerifier: z11.string().regex(/^[A-Za-z0-9\-._~]{32,128}$/)
});
var instagramUsernameSchema = z11.preprocess((value) => typeof value === "string" ? value.trim().replace(/^(https?:\/\/)?(www\.|m\.)?instagram\.com\//i, "").replace(/[/?#].*$/, "").replace(/^@/, "").toLowerCase() : value, z11.string().regex(/^[a-z0-9._]{1,30}$/, "\uC778\uC2A4\uD0C0\uADF8\uB7A8 \uC544\uC774\uB514\uB97C \uD655\uC778\uD574 \uC8FC\uC138\uC694."));
var instagramBioCodeRequestSchema = z11.object({
  username: instagramUsernameSchema
});
var instagramBioCodeResponseSchema = z11.object({
  /** 정리한 아이디 (@ 없이 소문자) */
  username: z11.string(),
  /** 프로필 소개에 넣을 코드 — 예: inflip-7K2Q9M */
  code: z11.string()
});

// src/common/platform-settings.schema.ts
import { DEFAULT_LANG, INTL_LOCALE, IpRightType as IpRightType6 } from "@inflip/shared";
import { z as z12 } from "zod";
var PLATFORM_SETTINGS_KEY = "platform";
var PLATFORM_ASSET_KEYS = [
  "favicon",
  "ogImage"
];
var platformAssetKeySchema = z12.enum(PLATFORM_ASSET_KEYS);
var PLATFORM_ASSET_RULES = {
  favicon: {
    mimeTypes: [
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/x-icon",
      "image/vnd.microsoft.icon"
    ],
    maxBytes: 1 * 1024 * 1024
  },
  ogImage: {
    mimeTypes: [
      "image/png",
      "image/jpeg",
      "image/webp"
    ],
    maxBytes: 5 * 1024 * 1024
  }
};
var platformAssetMetaSchema = z12.object({
  mimeType: z12.string(),
  size: z12.number().int().nonnegative(),
  updatedAt: z12.string()
});
function platformAssetPath(key, meta) {
  return `/platform/assets/${key}?v=${encodeURIComponent(meta.updatedAt)}`;
}
__name(platformAssetPath, "platformAssetPath");
var platformBankAccountSchema = z12.object({
  bankName: z12.string().min(1).max(50),
  accountNumber: z12.string().min(4).max(60),
  holderName: z12.string().min(1).max(50)
});
var platformPaymentSettingsSchema = z12.object({
  /** 무통장입금 입금 기한 — 주문 생성 시점 + N시간, 지나면 스케줄러가 만료 처리 */
  depositDueHours: z12.coerce.number().int().min(1).max(720),
  /** 추가금 입금 기한 — 판매자가 추가금을 요청한 시점 + N시간, 지나면 그 요청만 만료 (주문은 그대로) */
  addonDepositDueHours: z12.coerce.number().int().min(1).max(720),
  /**
  * 발급 지연 기준 — 입금 확인(PAID) 뒤 N일이 지나도록 발급되지 않은 주문.
  * 판매자 채팅방에 판매자에게만 보이는 안내를 매일 UTC 09:00 에 보내고, 어드민 대시보드·주문 목록에 표시한다.
  */
  licenseIssueDelayDays: z12.coerce.number().int().min(1).max(90),
  /**
  * 구매확정 기한 — 라이선스 발급 시점 + N일. 구매자가 확정하지 않아도 지나면 자동 확정되고 정산이 시작된다.
  * 발급 때 주문에 기한을 박아 두므로, 바꾸면 그 뒤 발급분부터 적용된다.
  */
  purchaseConfirmDays: z12.coerce.number().int().min(1).max(90),
  /**
  * 조건 협의 응답 기한 — 제안·수정 제안·제안 수정 시점 + N일. 그동안 상대가 응답하지 않으면 협의가 만료된다.
  * 제안할 때 기한을 박아 두므로, 바꾸면 그 뒤 제안분부터 적용된다.
  */
  proposalExpiryDays: z12.coerce.number().int().min(1).max(90),
  /**
  * 플랫폼 수수료(bp, 1% = 100) — 판매 대금·로열티에서 빼는 비율. 주문이 만들어질 때 주문에 고정되므로,
  * 바꾸면 그 뒤에 만들어지는 주문부터 적용된다 (진행 중 거래·로열티 계약은 기존 요율 유지).
  */
  platformFeeRateBp: z12.coerce.number().int().min(0, "\uC218\uC218\uB8CC\uB294 0~50% \uC0AC\uC774\uB85C \uC815\uD574 \uC8FC\uC138\uC694.").max(5e3, "\uC218\uC218\uB8CC\uB294 0~50% \uC0AC\uC774\uB85C \uC815\uD574 \uC8FC\uC138\uC694."),
  /** 구매자가 입금할 회사 계좌 (기본) */
  bankAccount: platformBankAccountSchema
});
var platformSiteTextSchema = z12.object({
  ogTitle: z12.string().min(1).max(120),
  ogDescription: z12.string().min(1).max(300),
  /** 사이트 <head> 에 검수 없이 그대로 주입되는 HTML — GA / 소유 확인 meta 등 */
  headScript: z12.string().max(2e4)
});
var platformSiteSettingsSchema = platformSiteTextSchema.extend({
  favicon: platformAssetMetaSchema.nullable(),
  ogImage: platformAssetMetaSchema.nullable()
});
var channelMinFollowersSchema = z12.object({
  YOUTUBE: z12.coerce.number().int().nonnegative(),
  INSTAGRAM: z12.coerce.number().int().nonnegative(),
  TIKTOK: z12.coerce.number().int().nonnegative(),
  THREADS: z12.coerce.number().int().nonnegative(),
  SOOP: z12.coerce.number().int().nonnegative(),
  PANDATV: z12.coerce.number().int().nonnegative()
});
function lowestMinFollowers(minFollowers2) {
  return Math.min(...Object.values(minFollowers2));
}
__name(lowestMinFollowers, "lowestMinFollowers");
var communityTierBoundariesSchema = z12.array(z12.coerce.number().int().min(1)).min(1).max(30).refine((arr) => arr.every((v, i) => i === 0 || arr[i - 1] < v), {
  message: "\uD2F0\uC5B4 \uACBD\uACC4\uAC12\uC740 \uC624\uB984\uCC28\uC21C\uC774\uACE0 \uC911\uBCF5\uC774 \uC5C6\uC5B4\uC57C \uD574\uC694."
});
var platformCommunitySettingsSchema = z12.object({
  /** 티어 경계값 (팔로워 수, 오름차순·중복 없음) — 각 값 N 은 "N+" 배지 */
  tierBoundaries: communityTierBoundariesSchema,
  /** 글을 쓸 수 있는 채널별 최소 팔로워 수 */
  minFollowersToPostByPlatform: channelMinFollowersSchema,
  /** 댓글을 쓸 수 있는 채널별 최소 팔로워 수 — 0 이면 그 채널 인증만 돼 있으면 가능 */
  minFollowersToCommentByPlatform: channelMinFollowersSchema,
  /** 옛 앱용 — 채널별 글쓰기 기준 중 가장 낮은 값 (서버가 채운다) */
  minFollowersToPost: z12.coerce.number().int().nonnegative(),
  /** 옛 앱용 — 채널별 댓글 기준 중 가장 낮은 값 (서버가 채운다) */
  minFollowersToComment: z12.coerce.number().int().nonnegative()
});
function formatFollowerBoundary(n, lang = DEFAULT_LANG) {
  if (lang === "ko") return `${formatFollowerCount(n)}+`;
  const short = new Intl.NumberFormat(INTL_LOCALE[lang], {
    notation: "compact",
    maximumFractionDigits: 2
  }).format(n);
  return `${short}+`;
}
__name(formatFollowerBoundary, "formatFollowerBoundary");
function formatFollowerCount(n) {
  if (n >= 1e8) return `${trimNumber(n / 1e8)}\uC5B5`;
  if (n >= 1e4) return `${trimNumber(n / 1e4)}\uB9CC`;
  if (n >= 1e3) return `${trimNumber(n / 1e3)}\uCC9C`;
  return String(n);
}
__name(formatFollowerCount, "formatFollowerCount");
function trimNumber(v) {
  return String(Number(v.toFixed(2)));
}
__name(trimNumber, "trimNumber");
function resolveCommunityTier(boundaries, followerCount) {
  let tier = null;
  for (const b of boundaries) {
    if (followerCount >= b) tier = b;
  }
  return tier;
}
__name(resolveCommunityTier, "resolveCommunityTier");
var sponsorshipCategorySchema = z12.object({
  /** 협찬에 저장되는 값 — 이름을 바꿔도 유지된다 */
  id: z12.string().trim().min(1).max(40),
  label: z12.string().trim().min(1, "\uC885\uBAA9 \uC774\uB984\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(20)
});
var platformSponsorshipSettingsSchema = z12.object({
  minFollowers: channelMinFollowersSchema,
  /** 협찬 종목 — 등록 시 하나를 고르고 목록에서 종목별로 거른다. 순서가 곧 표시 순서 */
  categories: z12.array(sponsorshipCategorySchema).min(1, "\uC885\uBAA9\uC744 1\uAC1C \uC774\uC0C1 \uB0A8\uACA8 \uC8FC\uC138\uC694").max(30).refine((list) => new Set(list.map((c) => c.id)).size === list.length, {
    message: "\uC885\uBAA9 id \uAC00 \uACB9\uCCD0\uC694."
  }).refine((list) => new Set(list.map((c) => c.label)).size === list.length, {
    message: "\uAC19\uC740 \uC774\uB984\uC758 \uC885\uBAA9\uC774 \uC788\uC5B4\uC694."
  })
});
var optionalText = /* @__PURE__ */ __name((max) => z12.string().trim().max(max), "optionalText");
var platformCompanySettingsSchema = z12.object({
  /** 상호 */
  businessName: optionalText(100),
  /** 대표자 */
  representative: optionalText(50),
  /** 사업자등록번호 */
  businessNumber: optionalText(20),
  /** 통신판매업 신고번호 */
  mailOrderNumber: optionalText(50),
  /** 사업장 주소 */
  address: optionalText(200),
  /** 호스팅 서비스 제공자 */
  hostingProvider: optionalText(100),
  /** 고객센터 이메일 */
  customerEmail: optionalText(200).refine((v) => v === "" || z12.string().email().safeParse(v).success, {
    message: "\uC774\uBA54\uC77C \uD615\uC2DD\uC774 \uC544\uB2C8\uC5D0\uC694."
  }),
  /** 고객센터 전화 */
  customerPhone: optionalText(30),
  /** 고객센터 운영 시간 */
  customerHours: optionalText(100),
  /** 개인정보 보호책임자 (성명 또는 직책) */
  privacyOfficerName: optionalText(50),
  /** 개인정보 보호책임자 연락처 (이메일·전화) */
  privacyOfficerContact: optionalText(200)
});
var POLICY_COMPANY_PLACEHOLDERS = {
  \uC0C1\uD638: "businessName",
  \uB300\uD45C\uC790: "representative",
  \uC0AC\uC5C5\uC790\uB4F1\uB85D\uBC88\uD638: "businessNumber",
  \uD1B5\uC2E0\uD310\uB9E4\uC5C5\uC2E0\uACE0\uBC88\uD638: "mailOrderNumber",
  \uC8FC\uC18C: "address",
  \uACE0\uAC1D\uC13C\uD130\uC774\uBA54\uC77C: "customerEmail",
  \uACE0\uAC1D\uC13C\uD130\uC804\uD654: "customerPhone",
  \uACE0\uAC1D\uC13C\uD130\uC6B4\uC601\uC2DC\uAC04: "customerHours",
  \uAC1C\uC778\uC815\uBCF4\uBCF4\uD638\uCC45\uC784\uC790: "privacyOfficerName",
  \uAC1C\uC778\uC815\uBCF4\uBCF4\uD638\uCC45\uC784\uC790\uC5F0\uB77D\uCC98: "privacyOfficerContact"
};
function fillCompanyPlaceholders(text, company) {
  return text.replace(/\{\{\s*([^{}\s]+)\s*\}\}/g, (whole, name) => {
    const field = POLICY_COMPANY_PLACEHOLDERS[name];
    if (!field) return whole;
    return company[field] || "-";
  });
}
__name(fillCompanyPlaceholders, "fillCompanyPlaceholders");
var SOCIAL_LINK_KEYS = [
  "instagram",
  "youtube",
  "tiktok",
  "x",
  "threads",
  "blog",
  "kakao"
];
var SOCIAL_LINK_LABELS = {
  instagram: "Instagram",
  youtube: "YouTube",
  tiktok: "TikTok",
  x: "X",
  threads: "Threads",
  blog: "\uBE14\uB85C\uADF8",
  kakao: "\uCE74\uCE74\uC624\uD1A1 \uCC44\uB110"
};
var SOCIAL_LINK_LABELS_I18N = {
  ko: SOCIAL_LINK_LABELS,
  en: {
    ...SOCIAL_LINK_LABELS,
    blog: "Blog",
    kakao: "KakaoTalk Channel"
  },
  ja: {
    ...SOCIAL_LINK_LABELS,
    blog: "\u30D6\u30ED\u30B0",
    kakao: "\u30AB\u30AB\u30AA\u30C8\u30FC\u30AF\u30C1\u30E3\u30F3\u30CD\u30EB"
  }
};
var socialUrlSchema = z12.string().trim().max(500).refine((v) => v === "" || /^https?:\/\//i.test(v) && z12.string().url().safeParse(v).success, {
  message: "http:// \uB610\uB294 https:// \uB85C \uC2DC\uC791\uD558\uB294 \uC8FC\uC18C\uB97C \uB123\uC5B4 \uC8FC\uC138\uC694."
});
var platformSocialLinksSchema = z12.object(Object.fromEntries(SOCIAL_LINK_KEYS.map((k) => [
  k,
  socialUrlSchema
])));
var LISTING_RIGHTS_LIMIT = 20;
var platformListingSettingsSchema = z12.object({
  maxRightsPerListing: z12.coerce.number().int().min(1, "\uAD8C\uB9AC \uC218\uB294 1~20\uAC1C \uC0AC\uC774\uB85C \uC815\uD574 \uC8FC\uC138\uC694.").max(LISTING_RIGHTS_LIMIT, "\uAD8C\uB9AC \uC218\uB294 1~20\uAC1C \uC0AC\uC774\uB85C \uC815\uD574 \uC8FC\uC138\uC694.")
});
var platformSettingsSchema = platformPaymentSettingsSchema.extend({
  site: platformSiteSettingsSchema,
  community: platformCommunitySettingsSchema,
  sponsorship: platformSponsorshipSettingsSchema,
  listing: platformListingSettingsSchema,
  company: platformCompanySettingsSchema,
  socialLinks: platformSocialLinksSchema
});
var catalogOptionSchema = z12.object({
  code: z12.string(),
  label: z12.string()
});
var platformCatalogSchema = z12.object({
  /** 정산·환불 계좌 은행 (PAYOUT_BANKS) */
  payoutBanks: z12.array(z12.string()),
  /** 신규 판매·탐색에 노출하는 권리 종류 (ACTIVE_IP_RIGHT_TYPES) */
  activeRightTypes: z12.array(z12.nativeEnum(IpRightType6)),
  /** 연락처 국가 번호 선택지 (PHONE_COUNTRY_CODES) — 없으면 옛 서버 */
  phoneCountryCodes: z12.array(catalogOptionSchema).optional(),
  /** 커뮤니티 신고 사유 (COMMUNITY_REPORT_REASONS, code 는 신고 요청의 reason) — 없으면 옛 서버 */
  communityReportReasons: z12.array(catalogOptionSchema).optional()
});
var siteConfigResponseSchema = platformSettingsSchema.extend({
  /** 앱 모델에서 선택 칸 — 이 칸이 생기기 전 서버에 붙어도 설정 전체를 못 읽는 일이 없게 (없으면 상한 없음, 서버가 검사) */
  listing: platformListingSettingsSchema.optional(),
  catalog: platformCatalogSchema
});
var updatePaymentSettingsRequestSchema = platformPaymentSettingsSchema;
var updateSiteSettingsRequestSchema = platformSiteTextSchema;
var updateCommunitySettingsRequestSchema = platformCommunitySettingsSchema.omit({
  minFollowersToPost: true,
  minFollowersToComment: true
});
var updateSponsorshipSettingsRequestSchema = platformSponsorshipSettingsSchema;
var updateListingSettingsRequestSchema = platformListingSettingsSchema;
var updateCompanySettingsRequestSchema = platformCompanySettingsSchema;
var updatePlatformSocialLinksRequestSchema = platformSocialLinksSchema;
var DEFAULT_PLATFORM_SETTINGS = {
  depositDueHours: 24,
  addonDepositDueHours: 24,
  licenseIssueDelayDays: 3,
  purchaseConfirmDays: 14,
  proposalExpiryDays: 7,
  platformFeeRateBp: 1e3,
  bankAccount: {
    bankName: "\uAD6D\uBBFC\uC740\uD589",
    accountNumber: "000000-00-000000 (\uAC1C\uC124 \uC804 placeholder)",
    holderName: "\uC778\uD50C\uB9BD"
  },
  site: {
    ogTitle: "Inflip \u2014 \uC778\uD50C\uB8E8\uC5B8\uC11C IP \uB9C8\uCF13\uD50C\uB808\uC774\uC2A4",
    ogDescription: "\uC778\uD50C\uB8E8\uC5B8\uC11C\uC758 \uCE90\uB9AD\uD130, \uCF58\uD150\uCE20, \uBE0C\uB79C\uB4DC IP\uB97C \uC9C1\uC811 \uC0AC\uACE0\uD30C\uB294 \uACF3. \uB77C\uC774\uC120\uC2A4 \uC870\uAC74\uC740 \uBA85\uD655\uD558\uAC8C, \uAC70\uB798\uB294 \uC548\uC804\uD558\uAC8C.",
    headScript: "",
    favicon: null,
    ogImage: null
  },
  community: {
    tierBoundaries: [
      2e4,
      5e4,
      1e5,
      15e4,
      2e5
    ],
    minFollowersToPostByPlatform: {
      YOUTUBE: 2e4,
      INSTAGRAM: 2e4,
      TIKTOK: 2e4,
      THREADS: 2e4,
      SOOP: 2e4,
      PANDATV: 2e4
    },
    minFollowersToCommentByPlatform: {
      YOUTUBE: 0,
      INSTAGRAM: 0,
      TIKTOK: 0,
      THREADS: 0,
      SOOP: 0,
      PANDATV: 0
    },
    minFollowersToPost: 2e4,
    minFollowersToComment: 0
  },
  sponsorship: {
    minFollowers: {
      YOUTUBE: 1e4,
      INSTAGRAM: 1e4,
      TIKTOK: 1e4,
      THREADS: 1e4,
      // 라이브 방송 플랫폼은 팔로워 규모가 작아 천 명부터 (2026-11-04)
      SOOP: 1e3,
      PANDATV: 1e3
    },
    categories: [
      {
        id: "food",
        label: "\uC74C\uC2DD\xB7\uCE74\uD398"
      },
      {
        id: "stay",
        label: "\uC219\uBC15"
      },
      {
        id: "photo",
        label: "\uC0AC\uC9C4\xB7\uCD2C\uC601"
      },
      {
        id: "beauty",
        label: "\uBDF0\uD2F0"
      },
      {
        id: "fashion",
        label: "\uD328\uC158"
      },
      {
        id: "product",
        label: "\uC81C\uD488 \uB9AC\uBDF0"
      },
      {
        id: "travel",
        label: "\uC5EC\uD589\xB7\uC561\uD2F0\uBE44\uD2F0"
      },
      {
        id: "fitness",
        label: "\uC6B4\uB3D9\xB7\uAC74\uAC15"
      },
      {
        id: "culture",
        label: "\uBB38\uD654\xB7\uACF5\uC5F0"
      },
      {
        id: "etc",
        label: "\uAE30\uD0C0"
      }
    ]
  },
  listing: {
    maxRightsPerListing: 1
  },
  company: {
    businessName: "",
    representative: "",
    businessNumber: "",
    mailOrderNumber: "",
    address: "",
    hostingProvider: "",
    customerEmail: "",
    customerPhone: "",
    customerHours: "",
    privacyOfficerName: "",
    privacyOfficerContact: ""
  },
  socialLinks: {
    instagram: "",
    youtube: "",
    tiktok: "",
    x: "",
    threads: "",
    blog: "",
    kakao: ""
  }
};

// src/common/app-versions.schema.ts
import { AppPlatform } from "@inflip/shared";
import { z as z13 } from "zod";
var appSemverSchema = z13.string().regex(/^\d+\.\d+\.\d+$/, "\uBC84\uC804\uC740 x.y.z \uD615\uC2DD\uC774\uC5B4\uC57C \uD574\uC694 (\uC608: 1.2.3)");
function compareAppVersions(a, b) {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i += 1) {
    if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0);
  }
  return 0;
}
__name(compareAppVersions, "compareAppVersions");
var appVersionResponseSchema = z13.object({
  platform: z13.nativeEnum(AppPlatform),
  /** 강제 업데이트 기준 — 이보다 낮으면 앱 사용 불가 */
  minVersion: appSemverSchema,
  /** 스토어에 출시된 최신 버전 */
  latestVersion: appSemverSchema,
  /** 강제 업데이트 화면 문구 — null 이면 클라이언트 기본 문구 */
  forceUpdateMessage: z13.string().max(500).nullable(),
  /** 선택 업데이트 안내 문구 — 아직 앱 노출 화면 없음 (예비) */
  optionalUpdateMessage: z13.string().max(500).nullable(),
  /** 업데이트 버튼이 여는 주소 — null 이면 클라이언트 기본 fallback */
  storeUrl: z13.string().url().max(500).nullable(),
  /** 웹사이트 앱 다운로드 카드에 노출할 QR 이미지 */
  qrImageUrl: z13.string().url().max(500).nullable(),
  updatedAt: z13.string().datetime()
});
var updateAppVersionRequestSchema = z13.object({
  minVersion: appSemverSchema,
  latestVersion: appSemverSchema,
  forceUpdateMessage: z13.string().max(500).nullable(),
  optionalUpdateMessage: z13.string().max(500).nullable(),
  storeUrl: z13.string().url().max(500).nullable(),
  qrImageUrl: z13.string().url().max(500).nullable()
}).refine((v) => compareAppVersions(v.minVersion, v.latestVersion) <= 0, {
  message: "\uCD5C\uC18C \uBC84\uC804\uC774 \uCD5C\uC2E0 \uBC84\uC804\uBCF4\uB2E4 \uB192\uC744 \uC218 \uC5C6\uC5B4\uC694",
  path: [
    "minVersion"
  ]
});

// src/user/sponsorships.schema.ts
import { SPONSORSHIP_MISSION_LINKS_MAX, SPONSORSHIP_MISSION_TEXT_MAX, SocialPlatform as SocialPlatform3, SponsorshipApplicationStatus, SponsorshipApprovalMode, SponsorshipRegion, SponsorshipStatus } from "@inflip/shared";
import { z as z14 } from "zod";
var SPONSORSHIP_TITLE_MAX = 100;
var SPONSORSHIP_BUSINESS_NAME_MAX = 100;
var SPONSORSHIP_DESCRIPTION_MAX = 5e3;
var SPONSORSHIP_BENEFIT_MAX = 2e3;
var SPONSORSHIP_LOCATION_MAX = 200;
var SPONSORSHIP_MESSAGE_MAX = 500;
var SPONSORSHIP_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
var SPONSORSHIP_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp"
];
var sponsorshipImageInputSchema = z14.object({
  mimeType: z14.enum(SPONSORSHIP_IMAGE_MIME_TYPES),
  /** 이미지 바이트의 base64 (data: 접두어 없이) */
  data: z14.string().min(1).max(Math.ceil(SPONSORSHIP_IMAGE_MAX_BYTES / 3) * 4, "\uC774\uBBF8\uC9C0\uB294 2MB \uC774\uD558\uB85C \uC62C\uB824 \uC8FC\uC138\uC694").regex(/^[A-Za-z0-9+/]+={0,2}$/, "\uC774\uBBF8\uC9C0 \uD615\uC2DD\uC774 \uC62C\uBC14\uB974\uC9C0 \uC54A\uC544\uC694")
});
var minFollowers = z14.number().int("\uAD6C\uB3C5\uC790\xB7\uD314\uB85C\uC6CC \uC218\uB294 \uC815\uC218\uB85C \uC785\uB825\uD574 \uC8FC\uC138\uC694").min(1, "\uAD6C\uB3C5\uC790\xB7\uD314\uB85C\uC6CC \uC218\uB294 1\uBA85 \uC774\uC0C1\uC774\uC5B4\uC57C \uD574\uC694").max(1e9);
var requirementShape = z14.object({
  YOUTUBE: minFollowers.optional(),
  INSTAGRAM: minFollowers.optional(),
  TIKTOK: minFollowers.optional(),
  THREADS: minFollowers.optional(),
  SOOP: minFollowers.optional(),
  PANDATV: minFollowers.optional()
}).strict();
var sponsorshipRequirementsSchema = requirementShape;
var sponsorshipCoordsSchema = z14.object({
  lat: z14.number().min(33).max(39),
  lng: z14.number().min(124).max(132)
});
var saveSponsorshipRequestSchema = z14.object({
  title: z14.string().trim().min(2, "\uC81C\uBAA9\uC740 2\uC790 \uC774\uC0C1 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(SPONSORSHIP_TITLE_MAX),
  businessName: z14.string().trim().min(1, "\uC5C5\uCCB4\uBA85\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(SPONSORSHIP_BUSINESS_NAME_MAX),
  description: z14.string().trim().min(10, "\uC18C\uAC1C\uB294 10\uC790 \uC774\uC0C1 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(SPONSORSHIP_DESCRIPTION_MAX),
  benefit: z14.string().trim().min(2, "\uD611\uCC2C\uC73C\uB85C \uC81C\uACF5\uD558\uB294 \uB0B4\uC6A9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(SPONSORSHIP_BENEFIT_MAX),
  /** 종목 — 어드민이 관리하는 종목 id (서버가 현재 목록에 있는지 확인) */
  categoryId: z14.string().trim().min(1, "\uD611\uCC2C \uC885\uBAA9\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694").max(40),
  region: z14.nativeEnum(SponsorshipRegion, {
    message: "\uC9C0\uC5ED\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694"
  }),
  /** 상세 위치 (동네·주소 등) */
  location: z14.string().trim().max(SPONSORSHIP_LOCATION_MAX).nullable().optional(),
  /** 상세 위치의 지도 좌표 (주소 검색으로 고른 경우) — 국내 범위만 */
  locationCoords: sponsorshipCoordsSchema.nullable().optional(),
  approvalMode: z14.nativeEnum(SponsorshipApprovalMode),
  /**
  * 지원자가 업체에 방문해야 하는지 — false 면 지원할 때 방문 예정일을 받지 않는다.
  * 화면은 늘 보낸다. 빠지면(이 항목 전의 화면) 예전처럼 전국·온라인만 방문 없음으로 본다
  */
  visitRequired: z14.boolean().optional(),
  /** 지원 조건은 필수 — 채널을 하나 이상 걸어야 등록된다 */
  requirements: requirementShape.refine((r) => Object.values(r).some((v) => v !== void 0), {
    message: "\uC9C0\uC6D0 \uC870\uAC74\uC744 \uD558\uB098 \uC774\uC0C1 \uC124\uC815\uD574 \uC8FC\uC138\uC694"
  }),
  /** 대표 이미지 (선택) — 바꿀 때만 보낸다. 이미지만 바꾸면 다시 심사하지 않는다. 지우기는 DELETE /sponsorships/:id/image */
  image: sponsorshipImageInputSchema.optional()
});
var SPONSORSHIP_SEARCH_MAX = 50;
var listSponsorshipsQuerySchema = pageQuerySchema.extend({
  region: z14.nativeEnum(SponsorshipRegion).optional(),
  categoryId: z14.string().trim().min(1).max(40).optional(),
  /** 제목·업체명·위치 부분 일치 검색어 (목록에 공개되는 항목만 — 상세 내용은 검색 대상이 아니다) */
  q: z14.string().trim().max(SPONSORSHIP_SEARCH_MAX).optional()
});
var sponsorshipSummarySchema = z14.object({
  id: z14.string().uuid(),
  ownerId: z14.string().uuid(),
  title: z14.string(),
  businessName: z14.string(),
  categoryId: z14.string(),
  /** 종목 이름 — 어드민이 종목을 지웠으면 null */
  categoryLabel: z14.string().nullable(),
  region: z14.nativeEnum(SponsorshipRegion),
  location: z14.string().nullable(),
  /** 대표 이미지 경로 (API 기준 상대 경로). null 이면 화면이 기본 표지를 그린다 */
  imagePath: z14.string().nullable(),
  status: z14.nativeEnum(SponsorshipStatus),
  /** 이 협찬의 지원 조건 — 목록에서도 보여 들어가 보기 전에 지원할 수 있는지 알 수 있다 */
  requirements: sponsorshipRequirementsSchema,
  /** 지원 조건 문구 — ["유튜브 구독자 3만 이상", …] (shared sponsorshipRequirementLabels). 없으면 옛 서버 */
  requirementLabels: z14.array(z14.string()).optional(),
  /**
  * 목록을 보는 사람이 이 협찬의 지원 조건을 충족하는지 — 열람 자격이 있는 인플루언서가 남의 협찬을 볼 때만.
  * 그 밖(비로그인·자격 없음·내 협찬·목록이 아닌 응답)은 null/없음
  */
  canApply: z14.boolean().nullable().optional(),
  createdAt: z14.string().datetime()
});
var sponsorshipResponseSchema = sponsorshipSummarySchema.extend({
  ownerName: z14.string(),
  description: z14.string(),
  benefit: z14.string(),
  approvalMode: z14.nativeEnum(SponsorshipApprovalMode),
  /** 지원자가 업체에 방문해야 하는지 */
  visitRequired: z14.boolean(),
  applicationCount: z14.number().int(),
  /** 어드민 반려 사유 — 반려됐을 때만, 등록자에게 보여준다 */
  rejectReason: z14.string().nullable(),
  /** 지도 좌표 — 상세(등록자·인증 인플루언서)에만. 없으면 지도 없이 주소만 */
  locationCoords: sponsorshipCoordsSchema.nullable()
});
var sponsorshipCategoryListSchema = z14.array(z14.object({
  id: z14.string(),
  label: z14.string()
}));
var sponsorshipPageSchema = pageResponseSchema(sponsorshipSummarySchema);
var sponsorshipEligibilitySchema = z14.object({
  eligible: z14.boolean(),
  /** 인증한 SNS 채널이 하나라도 있는지 — 열람 안내에서 '인증 필요'와 '수치 부족'을 나눈다 */
  verified: z14.boolean(),
  /** 내 인증 채널의 구독자·팔로워 수 — 비공개(수치 없음) 채널은 빠진다 */
  followers: z14.record(z14.nativeEnum(SocialPlatform3), z14.number().int()),
  /** 플랫폼 열람 기준 (어드민 설정) — 인증 채널 중 하나가 이 수치 이상이면 상세를 본다. 0 이면 인증만 하면 된다 */
  platformMinFollowers: z14.record(z14.nativeEnum(SocialPlatform3), z14.number().int()),
  /** 열람 기준 안내 문구 — ["유튜브 구독자 1만 이상", "스레드 인증", …] (shared sponsorshipViewConditions). 없으면 옛 서버 */
  viewConditions: z14.array(z14.string()).optional()
});
var applySponsorshipRequestSchema = z14.object({
  message: z14.string().trim().max(SPONSORSHIP_MESSAGE_MAX).optional(),
  /** 방문 예정일 (YYYY-MM-DD) — 방문이 필요한 협찬이면 필수 (서버가 지역을 보고 판단) */
  visitDate: z14.string().regex(/^\d{4}-\d{2}-\d{2}$/, "\uBC29\uBB38 \uC608\uC815\uC77C\uC744 \uB0A0\uC9DC\uB85C \uC785\uB825\uD574 \uC8FC\uC138\uC694").optional()
});
var SPONSORSHIP_REJECT_REASON_MAX = 500;
var rejectSponsorshipApplicationRequestSchema = z14.object({
  reason: z14.string().trim().min(2, "\uAC70\uC808 \uC0AC\uC720\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(SPONSORSHIP_REJECT_REASON_MAX, `\uAC70\uC808 \uC0AC\uC720\uB294 ${SPONSORSHIP_REJECT_REASON_MAX}\uC790\uAE4C\uC9C0 \uC4F8 \uC218 \uC788\uC5B4\uC694`)
});
var SPONSORSHIP_RECEIVED_STAGES = [
  "pending",
  "ongoing",
  "mission",
  "done"
];
var sponsorshipReceivedStageSchema = z14.enum(SPONSORSHIP_RECEIVED_STAGES);
function sponsorshipReceivedStageOf(status, missionSubmittedAt) {
  if (status === SponsorshipApplicationStatus.PENDING) return "pending";
  if (status === SponsorshipApplicationStatus.APPROVED) return missionSubmittedAt ? "mission" : "ongoing";
  return "done";
}
__name(sponsorshipReceivedStageOf, "sponsorshipReceivedStageOf");
var channelSnapshotSchema = z14.object({
  platform: z14.nativeEnum(SocialPlatform3),
  followerCount: z14.number().int().nullable(),
  profileUrl: z14.string()
});
var sponsorshipApplicationResponseSchema = z14.object({
  id: z14.string().uuid(),
  sponsorshipId: z14.string().uuid(),
  sponsorshipTitle: z14.string(),
  businessName: z14.string(),
  /** 협찬 표지 경로 (API 기준) — 없으면 화면이 기본 표지 */
  sponsorshipImagePath: z14.string().nullable(),
  applicantId: z14.string().uuid(),
  /** 탈퇴한 지원자면 "기존닉네임(탈퇴한 회원)", 보관 기간이 지나 익명화됐으면 "탈퇴한 회원" */
  applicantName: z14.string(),
  applicantAvatarUrl: z14.string().nullable(),
  /** 지원자가 탈퇴함 — 공개 프로필이 없어 화면은 프로필로 잇지 않는다 */
  applicantWithdrawn: z14.boolean(),
  status: z14.nativeEnum(SponsorshipApplicationStatus),
  message: z14.string().nullable(),
  /** 방문 예정일 (YYYY-MM-DD) — 온라인 협찬이면 null */
  visitDate: z14.string().nullable(),
  /** 지원 시점에 이 협찬의 지원 조건을 통과시킨 채널 (조건 없는 협찬이면 null) */
  qualifiedPlatform: z14.nativeEnum(SocialPlatform3).nullable(),
  qualifiedFollowerCount: z14.number().int().nullable(),
  /** 지원자의 현재 인증 채널 — 등록자가 지원자를 판단할 근거 */
  channels: z14.array(channelSnapshotSchema),
  createdAt: z14.string().datetime(),
  decidedAt: z14.string().datetime().nullable(),
  /** 거절 사유 — 거절된 지원만 (사유를 받기 전에 거절된 지원은 null) */
  rejectReason: z14.string().nullable(),
  /** 미션 완료 등록 — 확정된 지원자가 올린 글·링크. 등록 전이면 null·빈 배열 */
  missionText: z14.string().nullable(),
  missionLinks: z14.array(z14.string()),
  missionSubmittedAt: z14.string().datetime().nullable(),
  /** 등록자가 미션을 확인한 시각 — 이때 미션 완료 */
  missionConfirmedAt: z14.string().datetime().nullable(),
  /**
  * 등록자가 보는 목록에서만 — 이 지원자와 미션 완료까지 마친 수. completedHere 는 이 협찬,
  * completedWithOwner 는 등록자의 모든 협찬(지운 협찬 포함). 지원자 본인이 보는 응답은 null
  */
  applicantHistory: z14.object({
    completedHere: z14.number().int(),
    completedWithOwner: z14.number().int()
  }).nullable(),
  /** 단계 (sponsorshipReceivedStageOf) — 지원자 본인 화면은 미션 확인(mission)을 진행중으로 묶어 본다. 없으면 옛 서버 */
  stage: sponsorshipReceivedStageSchema.optional()
});
var sponsorshipApplicationDetailSchema = sponsorshipApplicationResponseSchema.extend({
  /** 지원한 때의 협찬 내용 — 등록자가 뒤에 고쳐도 그대로 */
  sponsorship: z14.object({
    title: z14.string(),
    businessName: z14.string(),
    description: z14.string(),
    benefit: z14.string(),
    categoryLabel: z14.string().nullable(),
    region: z14.nativeEnum(SponsorshipRegion),
    location: z14.string().nullable(),
    locationCoords: sponsorshipCoordsSchema.nullable(),
    approvalMode: z14.nativeEnum(SponsorshipApprovalMode),
    requirements: sponsorshipRequirementsSchema,
    /** 지원 조건 문구 (shared sponsorshipRequirementLabels). 없으면 옛 서버 */
    requirementLabels: z14.array(z14.string()).optional()
  }),
  /** 협찬을 올린 회원 — 확정된 뒤 업체와 대화하기 */
  sponsorshipOwnerId: z14.string().uuid(),
  /** 지금 협찬 상태 — 협찬이 지워졌으면 null */
  sponsorshipStatus: z14.nativeEnum(SponsorshipStatus).nullable(),
  /** 지원한 뒤 등록자가 협찬 내용을 바꿨는지 */
  sponsorshipChanged: z14.boolean(),
  /** 지금 미션 완료를 등록할 수 있는지 — 확정됐고 방문 예정일(지원자 시간대)이 됐으면. 없으면 옛 서버 */
  missionOpen: z14.boolean().optional(),
  /** 지금 지원을 취소할 수 있는지 — 승인 대기, 또는 확정됐지만 미션 등록 전. 없으면 옛 서버 */
  cancellable: z14.boolean().optional()
});
var sponsorshipMyStateSchema = z14.object({
  isOwner: z14.boolean(),
  application: z14.object({
    id: z14.string().uuid(),
    status: z14.nativeEnum(SponsorshipApplicationStatus),
    visitDate: z14.string().nullable()
  }).nullable(),
  canApply: z14.boolean(),
  /** 지원할 수 없는 이유 (canApply=false 일 때) */
  reason: z14.string().nullable(),
  /** 내가 스크랩했는지 */
  scrapped: z14.boolean(),
  /** 고를 수 있는 방문 예정일 (YYYY-MM-DD, 보는 사람 시간대의 오늘 ~ SPONSORSHIP_VISIT_MAX_DAYS_AHEAD 뒤). 없으면 옛 서버 */
  visitDateRange: z14.object({
    min: z14.string(),
    max: z14.string()
  }).optional()
});
var sponsorshipScrapStateSchema = z14.object({
  scrapped: z14.boolean()
});
var listSponsorshipScrapsQuerySchema = pageQuerySchema.pick({
  page: true
});
var SPONSORSHIP_VISIT_MAX_DAYS_AHEAD = 180;
var sponsorshipUnseenApprovalsSchema = z14.object({
  count: z14.number().int().nonnegative()
});
var SPONSORSHIP_MINE_FIRST_PAGE = 20;
var SPONSORSHIP_MINE_PAGE = 10;
var SPONSORSHIP_RECEIVED_PAGE = 10;
var cursorQuerySchema = z14.object({
  before: z14.string().uuid().optional(),
  limit: z14.coerce.number().int().min(1).max(100).default(SPONSORSHIP_MINE_PAGE)
});
var listMySponsorshipsQuerySchema = cursorQuerySchema;
var listReceivedApplicationsQuerySchema = cursorQuerySchema.extend({
  stage: sponsorshipReceivedStageSchema.optional()
});
var sponsorshipMineSummarySchema = z14.object({
  postedCount: z14.number().int().nonnegative(),
  stages: z14.object({
    pending: z14.number().int().nonnegative(),
    ongoing: z14.number().int().nonnegative(),
    mission: z14.number().int().nonnegative(),
    done: z14.number().int().nonnegative()
  }),
  /** 처리할 지원이 있는 협찬만 — 승인 대기·미션 확인 수 */
  todoBySponsorship: z14.record(z14.string().uuid(), z14.object({
    pending: z14.number().int(),
    missions: z14.number().int()
  }))
});
var sponsorshipReceivedTodoSchema = z14.object({
  pending: z14.number().int().nonnegative(),
  missions: z14.number().int().nonnegative()
});
var submitSponsorshipMissionSchema = z14.object({
  text: z14.string().trim().max(SPONSORSHIP_MISSION_TEXT_MAX).optional(),
  links: z14.array(z14.string().trim().url("\uC62C\uBC14\uB978 \uC8FC\uC18C\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(500)).max(SPONSORSHIP_MISSION_LINKS_MAX).default([])
}).refine((v) => Boolean(v.text) || v.links.length > 0, {
  message: "\uBBF8\uC158 \uB0B4\uC6A9\uC774\uB098 \uB9C1\uD06C\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694",
  path: [
    "text"
  ]
});
var sponsorshipAddressSearchQuerySchema = z14.object({
  q: z14.string().trim().min(2, "\uB450 \uAE00\uC790 \uC774\uC0C1 \uC785\uB825\uD574 \uC8FC\uC138\uC694").max(80)
});
var sponsorshipAddressSchema = z14.object({
  /** 고르면 위치 칸에 들어갈 주소 — 도로명 주소(건물 이름) */
  address: z14.string(),
  /** 지번 주소 — 함께 보여 줘 고르기 쉽게 (없으면 null) */
  jibunAddress: z14.string().nullable(),
  /** 장소 검색으로 찾았으면 장소 이름 */
  placeName: z14.string().nullable(),
  coords: sponsorshipCoordsSchema
});
var sponsorshipAddressSearchResponseSchema = z14.object({
  items: z14.array(sponsorshipAddressSchema)
});

// src/user/support.schema.ts
import { SUPPORT_INQUIRY_BODY_MAX, SUPPORT_INQUIRY_BODY_MIN, SUPPORT_INQUIRY_TITLE_MAX, SupportInquiryCategory, SupportInquiryStatus, SupportMessageAuthor } from "@inflip/shared";
import { z as z15 } from "zod";
var createSupportInquiryRequestSchema = z15.object({
  category: z15.nativeEnum(SupportInquiryCategory),
  title: z15.string().trim().min(2, "\uC81C\uBAA9\uC744 2\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694").max(SUPPORT_INQUIRY_TITLE_MAX, `\uC81C\uBAA9\uC740 ${SUPPORT_INQUIRY_TITLE_MAX}\uC790\uAE4C\uC9C0\uC608\uC694`),
  body: z15.string().trim().min(SUPPORT_INQUIRY_BODY_MIN, `\uB0B4\uC6A9\uC744 ${SUPPORT_INQUIRY_BODY_MIN}\uC790 \uC774\uC0C1 \uC801\uC5B4 \uC8FC\uC138\uC694`).max(SUPPORT_INQUIRY_BODY_MAX, `\uB0B4\uC6A9\uC740 ${SUPPORT_INQUIRY_BODY_MAX}\uC790\uAE4C\uC9C0\uC608\uC694`),
  /** 이 주문에 대한 문의 — 그 주문의 구매자·판매자만 */
  orderId: z15.string().uuid().optional()
});
var SUPPORT_INQUIRY_PAGE = 20;
var listSupportInquiriesQuerySchema = z15.object({
  before: z15.string().uuid().optional(),
  limit: z15.coerce.number().int().min(1).max(100).default(SUPPORT_INQUIRY_PAGE)
});
var createSupportInquiryResponseSchema = z15.object({
  id: z15.string().uuid()
});
var supportInquiryReplyRequestSchema = z15.object({
  body: z15.string().trim().min(1, "\uB0B4\uC6A9\uC744 \uC801\uC5B4 \uC8FC\uC138\uC694").max(SUPPORT_INQUIRY_BODY_MAX, `\uB0B4\uC6A9\uC740 ${SUPPORT_INQUIRY_BODY_MAX}\uC790\uAE4C\uC9C0\uC608\uC694`)
});
var supportInquiryOrderSchema = z15.object({
  id: z15.string().uuid(),
  listingTitle: z15.string().nullable(),
  role: z15.enum([
    "buyer",
    "seller"
  ])
});
var supportInquirySummarySchema = z15.object({
  id: z15.string().uuid(),
  category: z15.nativeEnum(SupportInquiryCategory),
  title: z15.string(),
  status: z15.nativeEnum(SupportInquiryStatus),
  order: supportInquiryOrderSchema.nullable(),
  /** 아직 안 본 관리자 답변이 있다 */
  hasNewAnswer: z15.boolean(),
  lastMessageAt: z15.string().datetime(),
  createdAt: z15.string().datetime()
});
var supportInquiryMessageSchema = z15.object({
  id: z15.string().uuid(),
  author: z15.nativeEnum(SupportMessageAuthor),
  body: z15.string(),
  createdAt: z15.string().datetime()
});
var supportInquiryDetailSchema = supportInquirySummarySchema.extend({
  messages: z15.array(supportInquiryMessageSchema),
  closedAt: z15.string().datetime().nullable()
});
var supportUnseenCountSchema = z15.object({
  count: z15.number().int()
});

export {
  payoutAccountSchema,
  simplePinSchema,
  createSimplePinRequestSchema,
  changeSimplePinRequestSchema,
  oauthProviderSchema,
  oauthReauthCredentialSchema,
  updatePayoutAccountRequestSchema,
  updateRefundAccountRequestSchema,
  socialLinksSchema,
  updateSocialLinksRequestSchema,
  updateMyProfileRequestSchema,
  emailSchema,
  signUpRequestSchema,
  signInRequestSchema,
  devSignInRequestSchema,
  devSignInAccountsResponseSchema,
  handoffSignInRequestSchema,
  logoutOthersResponseSchema,
  tokenPairResponseSchema,
  meResponseSchema,
  refreshRequestSchema,
  oauthSignInRequestSchema,
  oauthNeedsAgreementResponseSchema,
  oauthSignInResponseSchema,
  completeOauthSignupRequestSchema,
  purchaseAddonsSchema,
  proposalFormSpecSchema,
  listListingsQuerySchema,
  offerDurationSchema,
  offerDurationOptionSchema,
  brandScopeSchema,
  usageScopeSchema,
  forbiddenIndustriesInputSchema,
  listingOfferInputSchema,
  listingOfferResponseSchema,
  proposalPricingSchema,
  contractTermsSchema,
  LISTING_THUMBNAIL_WIDTH,
  LISTING_THUMBNAIL_HEIGHT,
  LISTING_THUMBNAIL_MAX_BYTES,
  LISTING_THUMBNAIL_MIME_TYPES,
  listingThumbnailInputSchema,
  listingThumbnailPath,
  listingSummarySchema,
  listingPageSchema,
  listMyListingScrapsQuerySchema,
  listingDetailSchema,
  createListingRequestSchema,
  updateListingRequestSchema,
  myListingSchema,
  scrapStateSchema,
  licenseDraftFormSpecSchema,
  createOrderItemSchema,
  createOrderRequestSchema,
  paymentResponseSchema,
  updateDepositorNameRequestSchema,
  updateDepositorNameResponseSchema,
  addonPaymentSchema,
  requestAddonPaymentSchema,
  requestRefundRequestSchema,
  orderRefundSummarySchema,
  orderViolationReportSummarySchema,
  orderDepositReturnSchema,
  orderTerminationSummarySchema,
  orderGroupMemberSchema,
  orderResponseSchema,
  orderGroupResponseSchema,
  listingOfferStateSchema,
  sellerOrderResponseSchema,
  issueLicenseRequestSchema,
  issueLicenseResponseSchema,
  licenseDraftSchema,
  licenseDraftResponseSchema,
  upsertLicenseDraftRequestSchema,
  confirmLicenseDraftRequestSchema,
  licenseAmendmentTermsSchema,
  licenseAmendmentSchema,
  licenseAmendmentListResponseSchema,
  licenseSuspensionSchema,
  licenseSuspensionListResponseSchema,
  declineLicenseAmendmentRequestSchema,
  cancelMyOrderRequestSchema,
  DISPUTE_EVIDENCE_MAX_FILES,
  DISPUTE_EVIDENCE_MAX_BYTES,
  DISPUTE_EVIDENCE_MIME_TYPES,
  disputeEvidenceMimeSchema,
  disputeEvidencePresignRequestSchema,
  disputeEvidencePresignResponseSchema,
  disputeEvidenceSchema,
  disputeOrderRequestSchema,
  reportViolationRequestSchema,
  ORDER_LIST_PAGE,
  ORDER_STATUS_GROUPS,
  listMyOrdersQuerySchema,
  orderIssueRowSchema,
  ordersSummarySchema,
  createRoyaltyReportRequestSchema,
  updateRoyaltyReportRequestSchema,
  sellerVoidRoyaltyReportRequestSchema,
  royaltyDepositNoticeRequestSchema,
  royaltyDepositNoticeResponseSchema,
  royaltyReportFileSchema,
  royaltyObjectionSchema,
  royaltyReportResponseSchema,
  royaltyTodoSchema,
  royaltyContractSchema,
  myRoyaltiesResponseSchema,
  VERIFIABLE_SOCIAL_PLATFORMS,
  verifiableSocialPlatformSchema,
  manualSocialPlatformSchema,
  socialPlatformSchema,
  socialChannelRequestStatusSchema,
  socialChannelSchema,
  mySocialChannelRequestSchema,
  socialChannelsResponseSchema,
  createSocialChannelRequestSchema,
  SOCIAL_CHANNEL_APP_SCHEMES,
  socialChannelAuthorizeQuerySchema,
  socialChannelAuthorizeResponseSchema,
  TIKTOK_APP_REDIRECT_PATH,
  tiktokAppVerifyRequestSchema,
  instagramUsernameSchema,
  instagramBioCodeRequestSchema,
  instagramBioCodeResponseSchema,
  PLATFORM_SETTINGS_KEY,
  PLATFORM_ASSET_KEYS,
  platformAssetKeySchema,
  PLATFORM_ASSET_RULES,
  platformAssetMetaSchema,
  platformAssetPath,
  platformBankAccountSchema,
  platformPaymentSettingsSchema,
  platformSiteTextSchema,
  platformSiteSettingsSchema,
  channelMinFollowersSchema,
  lowestMinFollowers,
  platformCommunitySettingsSchema,
  formatFollowerBoundary,
  formatFollowerCount,
  resolveCommunityTier,
  sponsorshipCategorySchema,
  platformSponsorshipSettingsSchema,
  platformCompanySettingsSchema,
  POLICY_COMPANY_PLACEHOLDERS,
  fillCompanyPlaceholders,
  SOCIAL_LINK_KEYS,
  SOCIAL_LINK_LABELS,
  SOCIAL_LINK_LABELS_I18N,
  platformSocialLinksSchema,
  LISTING_RIGHTS_LIMIT,
  platformListingSettingsSchema,
  platformSettingsSchema,
  catalogOptionSchema,
  platformCatalogSchema,
  siteConfigResponseSchema,
  updatePaymentSettingsRequestSchema,
  updateSiteSettingsRequestSchema,
  updateCommunitySettingsRequestSchema,
  updateSponsorshipSettingsRequestSchema,
  updateListingSettingsRequestSchema,
  updateCompanySettingsRequestSchema,
  updatePlatformSocialLinksRequestSchema,
  DEFAULT_PLATFORM_SETTINGS,
  appSemverSchema,
  compareAppVersions,
  appVersionResponseSchema,
  updateAppVersionRequestSchema,
  SPONSORSHIP_TITLE_MAX,
  SPONSORSHIP_BUSINESS_NAME_MAX,
  SPONSORSHIP_DESCRIPTION_MAX,
  SPONSORSHIP_BENEFIT_MAX,
  SPONSORSHIP_LOCATION_MAX,
  SPONSORSHIP_MESSAGE_MAX,
  SPONSORSHIP_IMAGE_MAX_BYTES,
  SPONSORSHIP_IMAGE_MIME_TYPES,
  sponsorshipImageInputSchema,
  sponsorshipRequirementsSchema,
  sponsorshipCoordsSchema,
  saveSponsorshipRequestSchema,
  SPONSORSHIP_SEARCH_MAX,
  listSponsorshipsQuerySchema,
  sponsorshipSummarySchema,
  sponsorshipResponseSchema,
  sponsorshipCategoryListSchema,
  sponsorshipPageSchema,
  sponsorshipEligibilitySchema,
  applySponsorshipRequestSchema,
  SPONSORSHIP_REJECT_REASON_MAX,
  rejectSponsorshipApplicationRequestSchema,
  SPONSORSHIP_RECEIVED_STAGES,
  sponsorshipReceivedStageSchema,
  sponsorshipReceivedStageOf,
  sponsorshipApplicationResponseSchema,
  sponsorshipApplicationDetailSchema,
  sponsorshipMyStateSchema,
  sponsorshipScrapStateSchema,
  listSponsorshipScrapsQuerySchema,
  SPONSORSHIP_VISIT_MAX_DAYS_AHEAD,
  sponsorshipUnseenApprovalsSchema,
  SPONSORSHIP_MINE_FIRST_PAGE,
  SPONSORSHIP_MINE_PAGE,
  SPONSORSHIP_RECEIVED_PAGE,
  listMySponsorshipsQuerySchema,
  listReceivedApplicationsQuerySchema,
  sponsorshipMineSummarySchema,
  sponsorshipReceivedTodoSchema,
  submitSponsorshipMissionSchema,
  sponsorshipAddressSearchQuerySchema,
  sponsorshipAddressSchema,
  sponsorshipAddressSearchResponseSchema,
  createSupportInquiryRequestSchema,
  SUPPORT_INQUIRY_PAGE,
  listSupportInquiriesQuerySchema,
  createSupportInquiryResponseSchema,
  supportInquiryReplyRequestSchema,
  supportInquiryOrderSchema,
  supportInquirySummarySchema,
  supportInquiryMessageSchema,
  supportInquiryDetailSchema,
  supportUnseenCountSchema
};
//# sourceMappingURL=chunk-IJGPTWBB.js.map